import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparkleEffect } from '../../components/SparkleEffect';
import { GlitterHeart } from '../../components/GlitterHeart';
import { HeartCrackIcon, DollarSignIcon, SparklesIcon, FlameIcon, StarIcon, Wand2Icon, TrophyIcon, FlagIcon, Dice1Icon, Dice2Icon, Dice3Icon, Dice4Icon, Dice5Icon, Dice6Icon, DicesIcon, CameraIcon, SkullIcon, CarIcon, BriefcaseIcon, GraduationCapIcon, BookOpenIcon, ZapIcon, TvIcon, HeartIcon } from 'lucide-react';
// --- TYPES ---
type PlayerColor = 'pink' | 'purple' | 'blue' | 'teal' | 'gold' | 'coral';
interface Job {
  id: string;
  name: string;
  emoji: string;
  wage: number;
  wageDisplay?: string;
  taxType: 'W2' | '1099';
  taxRate: number;
  effectDescription: string;
  flavorText: string;
  tier: 'bad' | 'good';
}
interface Player {
  id: string;
  name: string;
  color: PlayerColor;
  position: number;
  skipNextTurn: boolean;
  avatar: string | null;
  job: Job | null;
  money: number;
  turnsPlayed: number;
  roundsWithoutIncome: number;
  evolved: boolean;
  pathIndex: number | null;
  inSchool: boolean;
  schoolTurnsLeft: number;
  studentLoanDebt: number;
  chaosTriggersRemaining: number;
}
type Category = 'start' | 'heartbreak' | 'money' | 'glowup' | 'chaos' | 'blessing' | 'wildcard' | 'finish' | 'tax' | 'money_loss';
interface TileData {
  id: number;
  name: string;
  category: Category;
  actionText: string;
  effect: 'move' | 'skip' | 'roll_again' | 'none' | 'tax' | 'money_loss' | 'money_gain' | 'chaos_portal' | 'career_switch';
  effectValue?: number;
}
type GamePhase = 'setup' | 'path_choice' | 'job_assignment' | 'journey_start' | 'playing' | 'tax_event' | 'chaos_realm' | 'career_switch' | 'winner';
// --- JOBS ---
const ALL_JOBS: Job[] = [
// --- 🎓 COLLEGE → BEST JOB PATH (Path 0) ---
{
  id: 'corporate-remote',
  name: 'Corporate Remote Worker',
  emoji: '💻',
  wage: 32,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income, occasional burnout',
  flavorText: 'Camera off, pants off, productivity off',
  tier: 'good'
}, {
  id: 'nurse',
  name: 'Nurse',
  emoji: '🏥',
  wage: 35,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income, high stress',
  flavorText: 'Saving lives but cant save yourself',
  tier: 'good'
}, {
  id: 'engineer',
  name: 'Engineer',
  emoji: '🛠️',
  wage: 45,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'High stable income',
  flavorText: 'Building things and burning out',
  tier: 'good'
}, {
  id: 'accountant',
  name: 'Accountant',
  emoji: '📊',
  wage: 30,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'Numbers dont lie but your boss does',
  tier: 'good'
}, {
  id: 'marketing-mgr',
  name: 'Marketing Manager',
  emoji: '📣',
  wage: 34,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'Making things go viral except your paycheck',
  tier: 'good'
}, {
  id: 'hr-specialist',
  name: 'HR Specialist',
  emoji: '👥',
  wage: 28,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'Pretending to care professionally',
  tier: 'good'
}, {
  id: 'software-dev',
  name: 'Software Developer',
  emoji: '👨‍💻',
  wage: 48,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Highest stable income',
  flavorText: 'Debugging code and your life simultaneously',
  tier: 'good'
}, {
  id: 'project-mgr',
  name: 'Project Manager',
  emoji: '📋',
  wage: 40,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'High stable income',
  flavorText: 'Herding cats professionally',
  tier: 'good'
}, {
  id: 'teacher',
  name: 'Teacher',
  emoji: '🍎',
  wage: 27,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable but low income',
  flavorText: 'Teaching others what you barely survived',
  tier: 'good'
}, {
  id: 'office-mgr',
  name: 'Office Manager',
  emoji: '🏢',
  wage: 29,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'The only one holding this place together',
  tier: 'good'
}, {
  id: 'financial-analyst',
  name: 'Financial Analyst',
  emoji: '💰',
  wage: 42,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'High stable income',
  flavorText: 'Analyzing everyones money but yours',
  tier: 'good'
}, {
  id: 'healthcare-admin',
  name: 'Healthcare Administrator',
  emoji: '🏥',
  wage: 36,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'Paperwork and panic attacks',
  tier: 'good'
}, {
  id: 'it-specialist',
  name: 'IT Specialist',
  emoji: '🖥️',
  wage: 38,
  taxType: 'W2',
  taxRate: 0.22,
  effectDescription: 'Stable income',
  flavorText: 'Have you tried turning it off and on again?',
  tier: 'good'
},
// --- 📚 HS / GED → GOOD JOB PATH (Path 1) ---
{
  id: 'call-center',
  name: 'Call Center Agent',
  emoji: '📞',
  wage: 18,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Please hold while I hold back tears',
  tier: 'bad'
}, {
  id: 'retail-mgr',
  name: 'Retail Manager',
  emoji: '🛍️',
  wage: 22,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'The customer is NOT always right',
  tier: 'bad'
}, {
  id: 'warehouse-supervisor',
  name: 'Warehouse Supervisor',
  emoji: '📦',
  wage: 21,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'My back, my knees, my will to live',
  tier: 'bad'
}, {
  id: 'delivery-driver',
  name: 'Delivery Driver',
  emoji: '🚚',
  wage: 20,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Your back hurts but the tips help',
  tier: 'bad'
}, {
  id: 'admin-assistant',
  name: 'Administrative Assistant',
  emoji: '📋',
  wage: 19,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Organizing someone elses life',
  tier: 'bad'
}, {
  id: 'bank-teller',
  name: 'Bank Teller',
  emoji: '🏦',
  wage: 18,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Handling money you cant keep',
  tier: 'bad'
}, {
  id: 'customer-service',
  name: 'Customer Service Representative',
  emoji: '💬',
  wage: 17,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Smiling through the chaos',
  tier: 'bad'
}, {
  id: 'security-guard',
  name: 'Security Guard',
  emoji: '🛡️',
  wage: 19,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Guarding everything except your peace',
  tier: 'bad'
}, {
  id: 'medical-assistant',
  name: 'Medical Assistant',
  emoji: '🩺',
  wage: 20,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Open wide and pray for benefits',
  tier: 'bad'
}, {
  id: 'receptionist',
  name: 'Receptionist',
  emoji: '🏢',
  wage: 17,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Gatekeeping the entire office',
  tier: 'bad'
}, {
  id: 'shift-supervisor',
  name: 'Shift Supervisor',
  emoji: '⏰',
  wage: 21,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'All the responsibility, half the pay',
  tier: 'bad'
}, {
  id: 'factory-worker',
  name: 'Factory Worker',
  emoji: '🏭',
  wage: 20,
  taxType: 'W2',
  taxRate: 0.18,
  effectDescription: 'Moderate steady income',
  flavorText: 'Rinse and repeat until retirement',
  tier: 'bad'
},
// --- ⚡ DROPOUT → MINIMUM WAGE PATH (Path 2) ---
{
  id: 'fast-food',
  name: 'Fast Food Worker',
  emoji: '🍔',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, unstable',
  flavorText: 'Sir this is a Wendys',
  tier: 'bad'
}, {
  id: 'retail-associate',
  name: 'Retail Associate',
  emoji: '🛍️',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, unstable',
  flavorText: 'Folding clothes that will be ruined in 5 mins',
  tier: 'bad'
}, {
  id: 'gig-driver',
  name: 'Gig Driver',
  emoji: '🚗',
  wage: 22,
  wageDisplay: '$8-$35/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Made $100… kept $12 after gas and life choices',
  tier: 'good'
}, {
  id: 'house-cleaner',
  name: 'House Cleaner',
  emoji: '🧽',
  wage: 16,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, physical work',
  flavorText: 'Found something weird under the bed...',
  tier: 'bad'
}, {
  id: 'dishwasher',
  name: 'Dishwasher',
  emoji: '🍽️',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, physical work',
  flavorText: 'Scrubbing plates and scrubbing by',
  tier: 'bad'
}, {
  id: 'stocker',
  name: 'Stocker',
  emoji: '📦',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, physical work',
  flavorText: 'Night shifts and weird customers',
  tier: 'bad'
}, {
  id: 'day-labor',
  name: 'Day Labor Worker',
  emoji: '🛠️',
  wage: 17,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, unstable',
  flavorText: 'Heavy lifting for light pay',
  tier: 'bad'
}, {
  id: 'gas-station',
  name: 'Gas Station Clerk',
  emoji: '⛽',
  wage: 15,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, unstable',
  flavorText: 'Selling lottery tickets you wish you won',
  tier: 'bad'
}, {
  id: 'lawn-care',
  name: 'Lawn Care Worker',
  emoji: '🌱',
  wage: 16,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, physical work',
  flavorText: 'Making yards pretty while yours falls apart',
  tier: 'bad'
}, {
  id: 'warehouse-worker',
  name: 'Warehouse Worker',
  emoji: '📦',
  wage: 17,
  taxType: 'W2',
  taxRate: 0.15,
  effectDescription: 'Low income, physical work',
  flavorText: 'Permanent stress temporary paycheck',
  tier: 'bad'
}, {
  id: 'babysitting',
  name: 'Babysitting / Odd Jobs',
  emoji: '👶',
  wage: 15,
  taxType: '1099',
  taxRate: 0.3,
  effectDescription: 'Low income, unstable',
  flavorText: 'Raising other peoples kids for minimum wage',
  tier: 'bad'
}, {
  id: 'thrift-flipper',
  name: 'Thrift Flipper',
  emoji: '🧢',
  wage: 12,
  taxType: '1099',
  taxRate: 0.3,
  effectDescription: 'Low income, unstable',
  flavorText: 'One persons trash is your income',
  tier: 'bad'
},
// --- 📺 SELF-EMPLOYED / STREAMING PATH (Path 3) ---
{
  id: 'streamer',
  name: 'Streamer',
  emoji: '🎮',
  wage: 20,
  wageDisplay: '$0-$40/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Going live to 2 people… but one viral moment changes everything',
  tier: 'good'
}, {
  id: 'content-creator',
  name: 'Content Creator',
  emoji: '📱',
  wage: 28,
  wageDisplay: '$5-$50/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Dancing for pennies until the brand deal hits',
  tier: 'good'
}, {
  id: 'freelancer',
  name: 'Freelancer',
  emoji: '💻',
  wage: 40,
  wageDisplay: '$20-$60/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Chasing invoices instead of dreams',
  tier: 'good'
}, {
  id: 'small-biz',
  name: 'Small Business Owner',
  emoji: '🛍️',
  wage: 45,
  wageDisplay: '$10-$80/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'You invested in yourself… one big deal changes everything',
  tier: 'good'
}, {
  id: 'online-seller',
  name: 'Online Seller',
  emoji: '📦',
  wage: 30,
  wageDisplay: '$15-$45/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Packing orders at 3 AM',
  tier: 'good'
}, {
  id: 'dropshipper',
  name: 'Dropshipper',
  emoji: '📊',
  wage: 35,
  wageDisplay: '$0-$70/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Selling things youve never even seen',
  tier: 'good'
}, {
  id: 'social-media-mgr',
  name: 'Social Media Manager',
  emoji: '📣',
  wage: 30,
  wageDisplay: '$20-$40/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Chronically online for a living',
  tier: 'good'
}, {
  id: 'photographer',
  name: 'Photographer',
  emoji: '📸',
  wage: 50,
  wageDisplay: '$25-$75/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Capturing moments, missing paychecks',
  tier: 'good'
}, {
  id: 'hair-stylist',
  name: 'Hair Stylist',
  emoji: '💇‍♀️',
  wage: 35,
  wageDisplay: '$20-$50/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Part-time therapist, full-time artist',
  tier: 'good'
}, {
  id: 'nail-tech',
  name: 'Nail Tech',
  emoji: '💅',
  wage: 32,
  wageDisplay: '$18-$45/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Breathing acrylic dust for the bag',
  tier: 'good'
}, {
  id: 'personal-trainer',
  name: 'Personal Trainer',
  emoji: '🏋️‍♀️',
  wage: 43,
  wageDisplay: '$25-$60/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Getting paid to yell at people',
  tier: 'good'
}, {
  id: 'coach-consultant',
  name: 'Coach / Consultant',
  emoji: '🧠',
  wage: 65,
  wageDisplay: '$30-$100/hr variable',
  taxType: '1099',
  taxRate: 0.35,
  effectDescription: 'Unpredictable income, chance for BIG BREAK 💥',
  flavorText: 'Selling advice you barely follow yourself',
  tier: 'good'
}];
// --- CONSTANTS ---
const PLAYER_COLORS: Record<PlayerColor, string> = {
  pink: 'bg-pink-500 shadow-pink-500/50',
  purple: 'bg-purple-500 shadow-purple-500/50',
  blue: 'bg-blue-500 shadow-blue-500/50',
  teal: 'bg-teal-500 shadow-teal-500/50',
  gold: 'bg-amber-400 shadow-amber-400/50',
  coral: 'bg-rose-400 shadow-rose-400/50'
};
const PLAYER_BORDER: Record<PlayerColor, string> = {
  pink: 'border-pink-400',
  purple: 'border-purple-400',
  blue: 'border-blue-400',
  teal: 'border-teal-400',
  gold: 'border-amber-300',
  coral: 'border-rose-300'
};
const CATEGORY_STYLES: Record<Category, {
  glow: string;
  border: string;
  bg: string;
  icon: React.ElementType;
}> = {
  start: {
    glow: 'shadow-[0_0_20px_rgba(255,255,255,0.8)]',
    border: 'border-white',
    bg: 'bg-gradient-to-br from-pink-300 via-purple-300 to-blue-300',
    icon: FlagIcon
  },
  heartbreak: {
    glow: 'shadow-[0_0_15px_rgba(244,63,94,0.4)]',
    border: 'border-rose-300',
    bg: 'bg-rose-50',
    icon: HeartCrackIcon
  },
  money: {
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    icon: DollarSignIcon
  },
  glowup: {
    glow: 'shadow-[0_0_15px_rgba(168,85,247,0.4)]',
    border: 'border-purple-300',
    bg: 'bg-purple-50',
    icon: SparklesIcon
  },
  chaos: {
    glow: 'shadow-[0_0_15px_rgba(249,115,22,0.4)]',
    border: 'border-orange-300',
    bg: 'bg-orange-50',
    icon: FlameIcon
  },
  blessing: {
    glow: 'shadow-[0_0_15px_rgba(20,184,166,0.4)]',
    border: 'border-teal-300',
    bg: 'bg-teal-50',
    icon: StarIcon
  },
  wildcard: {
    glow: 'shadow-[0_0_20px_rgba(236,72,153,0.5)]',
    border: 'border-pink-300',
    bg: 'bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50',
    icon: Wand2Icon
  },
  finish: {
    glow: 'shadow-[0_0_30px_rgba(251,191,36,0.6)]',
    border: 'border-amber-300',
    bg: 'bg-gradient-to-br from-amber-100 via-yellow-200 to-amber-300',
    icon: TrophyIcon
  },
  tax: {
    glow: 'shadow-[0_0_20px_rgba(220,38,38,0.5)]',
    border: 'border-red-400',
    bg: 'bg-gradient-to-br from-red-50 via-red-50 to-orange-50',
    icon: SkullIcon
  },
  money_loss: {
    glow: 'shadow-[0_0_18px_rgba(239,68,68,0.45)]',
    border: 'border-red-300',
    bg: 'bg-gradient-to-br from-red-50 via-rose-50 to-pink-50',
    icon: DollarSignIcon
  }
};
// --- TILES (30 tiles) ---
const TILES: TileData[] = [{
  id: 0,
  name: 'The Journey Begins',
  category: 'start',
  actionText: 'Ready to trip through life?',
  effect: 'none'
}, {
  id: 1,
  name: 'Your Ex Texted',
  category: 'heartbreak',
  actionText: 'Take a shot or skip a turn',
  effect: 'skip'
}, {
  id: 3,
  name: 'Car Broke Down',
  category: 'chaos',
  actionText: 'Move back 1 space',
  effect: 'move',
  effectValue: -1
}, {
  id: 4,
  name: 'New Haircut, New You',
  category: 'glowup',
  actionText: 'Roll again!',
  effect: 'roll_again'
}, {
  id: 5,
  name: 'Found $20 in Old Jeans',
  category: 'blessing',
  actionText: 'Move ahead 1 space',
  effect: 'move',
  effectValue: 1
}, {
  id: 6,
  name: 'Got Ghosted Again',
  category: 'heartbreak',
  actionText: 'Skip next turn',
  effect: 'skip'
}, {
  id: 7,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Host picks your fate',
  effect: 'none'
}, {
  id: 9,
  name: 'Started Therapy',
  category: 'glowup',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 10,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'The IRS has entered the chat. ALL players get taxed!',
  effect: 'tax'
}, {
  id: 11,
  name: 'Stranger Paid for Coffee',
  category: 'blessing',
  actionText: 'Move ahead 1 space',
  effect: 'move',
  effectValue: 1
}, {
  id: 12,
  name: 'Caught Them Cheating',
  category: 'heartbreak',
  actionText: 'Move back 3 spaces',
  effect: 'move',
  effectValue: -3
}, {
  id: 14,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Swap places with any player',
  effect: 'none'
}, {
  id: 15,
  name: 'Gym Glow Up Era',
  category: 'glowup',
  actionText: 'Roll again!',
  effect: 'roll_again'
}, {
  id: 16,
  name: 'Identity Stolen',
  category: 'chaos',
  actionText: 'Move back 2 spaces',
  effect: 'move',
  effectValue: -2
}, {
  id: 17,
  name: 'Dream Job Called Back',
  category: 'blessing',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 18,
  name: 'Toxic Friend Exposed',
  category: 'heartbreak',
  actionText: 'Skip a turn',
  effect: 'skip'
}, {
  id: 20,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'Uncle Sam wants his cut. ALL players get taxed!',
  effect: 'tax'
}, {
  id: 21,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Everyone moves back 1 except you',
  effect: 'none'
}, {
  id: 22,
  name: 'Apartment Flooded',
  category: 'chaos',
  actionText: 'Move back 2 spaces',
  effect: 'move',
  effectValue: -2
}, {
  id: 23,
  name: 'Met Your Person',
  category: 'blessing',
  actionText: 'Move ahead 3 spaces',
  effect: 'move',
  effectValue: 3
}, {
  id: 24,
  name: 'Wedding Called Off',
  category: 'heartbreak',
  actionText: 'Move back 3 spaces',
  effect: 'move',
  effectValue: -3
}, {
  id: 26,
  name: 'Wrote Your First Book',
  category: 'glowup',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 27,
  name: 'Lost Your Keys... Again',
  category: 'chaos',
  actionText: 'Skip a turn',
  effect: 'skip'
}, {
  id: 28,
  name: '💸 TAX SEASON',
  category: 'tax',
  actionText: 'Final tax hit before the finish! ALL players get taxed!',
  effect: 'tax'
}, {
  id: 29,
  name: 'You Made It, Babe!',
  category: 'finish',
  actionText: 'Winner winner!',
  effect: 'none'
},
// --- 💸 UNIVERSAL MONEY STRUGGLE TILES ---
// Bills / Adulting Pain
{
  id: 30,
  name: 'Rent Went Up 😬',
  category: 'money_loss',
  actionText: 'Rent went up this month...',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 31,
  name: 'Utility Bill Shock ⚡',
  category: 'money_loss',
  actionText: 'Utility bill way higher than expected',
  effect: 'money_loss',
  effectValue: -100
}, {
  id: 32,
  name: 'Internet Bill Up',
  category: 'money_loss',
  actionText: 'Internet bill randomly increased',
  effect: 'money_loss',
  effectValue: -20
}, {
  id: 33,
  name: 'Forgot a Subscription 💸',
  category: 'money_loss',
  actionText: 'Subscription you forgot about hit again',
  effect: 'money_loss',
  effectValue: -25
}, {
  id: 34,
  name: 'Late Fee 😒',
  category: 'money_loss',
  actionText: 'Late fee added to your bill',
  effect: 'money_loss',
  effectValue: -35
},
// Life Expenses
{
  id: 35,
  name: 'Gas Prices Spike ⛽',
  category: 'money_loss',
  actionText: 'Gas prices are insane right now',
  effect: 'money_loss',
  effectValue: -50
}, {
  id: 36,
  name: 'Car Maintenance 🛠️',
  category: 'money_loss',
  actionText: 'Car needs maintenance... again',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 37,
  name: 'Flat Tire 😭',
  category: 'money_loss',
  actionText: "Flat tire… and you're already late for work",
  effect: 'money_loss',
  effectValue: -100
}, {
  id: 38,
  name: 'Speeding Ticket 🚓',
  category: 'money_loss',
  actionText: 'Caught going 15 over...',
  effect: 'money_loss',
  effectValue: -275
}, {
  id: 39,
  name: 'Car Got Towed 😭',
  category: 'money_loss',
  actionText: 'Towed… pay to get it back',
  effect: 'money_loss',
  effectValue: -350
},
// Health / Real Life
{
  id: 40,
  name: 'Doctor Visit 🏥',
  category: 'money_loss',
  actionText: 'Doctor visit… not covered by insurance',
  effect: 'money_loss',
  effectValue: -700
}, {
  id: 41,
  name: 'Prescription Cost 💊',
  category: 'money_loss',
  actionText: 'Prescription cost hits different',
  effect: 'money_loss',
  effectValue: -300
}, {
  id: 42,
  name: 'Dental Expense 😬',
  category: 'money_loss',
  actionText: 'Unexpected dental expense',
  effect: 'money_loss',
  effectValue: -500
},
// Bad Decisions
{
  id: 43,
  name: 'Retail Therapy 🛍️',
  category: 'money_loss',
  actionText: 'Retail therapy got you again',
  effect: 'money_loss',
  effectValue: -450
}, {
  id: 44,
  name: 'Impulse Buy 😅',
  category: 'money_loss',
  actionText: 'Impulse buy regret is real',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 45,
  name: 'Ordered Food Again 🍔',
  category: 'money_loss',
  actionText: 'Ordered food instead of cooking',
  effect: 'money_loss',
  effectValue: -20
}, {
  id: 46,
  name: 'Boredom Spending',
  category: 'money_loss',
  actionText: 'Spent money out of pure boredom',
  effect: 'money_loss',
  effectValue: -25
}, {
  id: 47,
  name: 'TikTok Made Me Buy It 😭',
  category: 'money_loss',
  actionText: 'TikTok made you buy it...',
  effect: 'money_loss',
  effectValue: -60
},
// Bank / Money System Pain
{
  id: 48,
  name: 'Credit Card Interest 😩',
  category: 'money_loss',
  actionText: 'Credit card interest hit hard',
  effect: 'money_loss',
  effectValue: -28
}, {
  id: 49,
  name: 'Mystery Bank Fee',
  category: 'money_loss',
  actionText: "Bank fee you didn't understand",
  effect: 'money_loss',
  effectValue: -30
}, {
  id: 50,
  name: 'Double Charged 💀',
  category: 'money_loss',
  actionText: 'Payment processed twice!',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 51,
  name: 'Low Balance Fee 😒',
  category: 'money_loss',
  actionText: 'Low balance fee hit again',
  effect: 'money_loss',
  effectValue: -35
},
// Low-Key Stress Money Hits
{
  id: 52,
  name: 'Helped Someone Out 💔',
  category: 'money_loss',
  actionText: 'Gave money to someone who needed it',
  effect: 'money_loss',
  effectValue: -5
}, {
  id: 53,
  name: 'Bought a Meal ❤️',
  category: 'money_loss',
  actionText: 'Bought someone a meal',
  effect: 'money_loss',
  effectValue: -12
}, {
  id: 54,
  name: 'TikTok Gifts 😭',
  category: 'money_loss',
  actionText: 'Threw too many TikTok gifts',
  effect: 'money_loss',
  effectValue: -100
},
// Extra scattered hits
{
  id: 55,
  name: 'Parking Ticket 🅿️',
  category: 'money_loss',
  actionText: 'Forgot to feed the meter',
  effect: 'money_loss',
  effectValue: -75
}, {
  id: 56,
  name: 'Phone Screen Cracked 📱',
  category: 'money_loss',
  actionText: 'Dropped your phone face down...',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 57,
  name: 'Pet Vet Bill 🐾',
  category: 'money_loss',
  actionText: 'Your fur baby needed the vet',
  effect: 'money_loss',
  effectValue: -400
},
// --- 💸 POSITIVE MONEY WINS ---
// Small Wins
{
  id: 58,
  name: 'Found Money in Your Pocket 😄',
  category: 'money',
  actionText: 'Check those pockets more often!',
  effect: 'money_gain',
  effectValue: 20
}, {
  id: 59,
  name: 'Got Paid Back 💸',
  category: 'money',
  actionText: 'They actually remembered!',
  effect: 'money_gain',
  effectValue: 50
}, {
  id: 60,
  name: 'Cashback Hit Your Account 💳',
  category: 'money',
  actionText: 'Free money is the best money',
  effect: 'money_gain',
  effectValue: 25
}, {
  id: 61,
  name: "Didn't Order Food for Once 🍔",
  category: 'money',
  actionText: 'Your wallet thanks you',
  effect: 'money_gain',
  effectValue: 30
}, {
  id: 62,
  name: 'Stuck to Your Budget 📊',
  category: 'money',
  actionText: 'Look at you being responsible!',
  effect: 'money_gain',
  effectValue: 75
}, {
  id: 63,
  name: 'Split Bill Win 😌',
  category: 'money',
  actionText: 'Paid less than expected!',
  effect: 'money_gain',
  effectValue: 40
}, {
  id: 64,
  name: 'Coupon Actually Worked 😭',
  category: 'money',
  actionText: 'Against all odds...',
  effect: 'money_gain',
  effectValue: 15
}, {
  id: 65,
  name: 'Free Drink/Meal Hookup 🍹',
  category: 'money',
  actionText: 'Someone came through!',
  effect: 'money_gain',
  effectValue: 25
}, {
  id: 66,
  name: 'Someone Covered You 🙌',
  category: 'money',
  actionText: 'Good people still exist',
  effect: 'money_gain',
  effectValue: 35
},
// Medium Wins
{
  id: 67,
  name: 'Tax Refund Hit 🙌',
  category: 'money',
  actionText: 'Uncle Sam gave something back!',
  effect: 'money_gain',
  effectValue: 400
}, {
  id: 68,
  name: 'Extra Hours Paid Off ⏰',
  category: 'money',
  actionText: 'That overtime was worth it',
  effect: 'money_gain',
  effectValue: 150
}, {
  id: 69,
  name: "Sold Something You Didn't Need 📦",
  category: 'money',
  actionText: 'Declutter = profit',
  effect: 'money_gain',
  effectValue: 120
}, {
  id: 70,
  name: 'Refund Finally Processed 😩',
  category: 'money',
  actionText: 'Only took 3 weeks...',
  effect: 'money_gain',
  effectValue: 200
}, {
  id: 71,
  name: 'Random Discount at Checkout 🛍️',
  category: 'money',
  actionText: 'Price drop surprise!',
  effect: 'money_gain',
  effectValue: 100
}, {
  id: 72,
  name: 'Side Money Came Through 💻',
  category: 'money',
  actionText: 'Hustle pays off sometimes',
  effect: 'money_gain',
  effectValue: 180
}, {
  id: 73,
  name: 'Unexpected Cash Gift 🎁',
  category: 'money',
  actionText: 'Somebody loves you!',
  effect: 'money_gain',
  effectValue: 250
}, {
  id: 74,
  name: 'Bill Was Cheaper Than Expected 😮‍💨',
  category: 'money',
  actionText: "Wait... that's it??",
  effect: 'money_gain',
  effectValue: 130
},
// Big Wins
{
  id: 75,
  name: 'Saved Way More Than Expected 😭',
  category: 'money',
  actionText: 'Accidentally responsible!',
  effect: 'money_gain',
  effectValue: 600
}, {
  id: 76,
  name: 'Someone Came Through BIG 💖',
  category: 'money',
  actionText: 'Real ones show up when it matters',
  effect: 'money_gain',
  effectValue: 700
}, {
  id: 77,
  name: 'Emergency Cost Less Than Expected 😮‍💨',
  category: 'money',
  actionText: "Could've been so much worse",
  effect: 'money_gain',
  effectValue: 550
}, {
  id: 78,
  name: 'Covered Everything This Month 🙌',
  category: 'money',
  actionText: 'Bills paid, food bought, still breathing',
  effect: 'money_gain',
  effectValue: 500
}, {
  id: 79,
  name: 'Financial Stress Eased Up 😩✨',
  category: 'money',
  actionText: 'Finally catching a break',
  effect: 'money_gain',
  effectValue: 650
},
// --- 🎓 PATH-SPECIFIC NEGATIVE TILES: College → Best Job ---
{
  id: 80,
  name: 'Student Loan Payment Hit 😭',
  category: 'money_loss',
  actionText: 'Sallie Mae never forgets...',
  effect: 'money_loss',
  effectValue: -300
}, {
  id: 81,
  name: "Degree Didn't Match the Pay 😬",
  category: 'money_loss',
  actionText: '4 years for THIS salary?',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 82,
  name: 'Burnout… Unpaid Time Off 😩',
  category: 'money_loss',
  actionText: 'Your body said no but bills said yes',
  effect: 'money_loss',
  effectValue: -250
}, {
  id: 83,
  name: 'Job Relocation Cost You 💼',
  category: 'money_loss',
  actionText: 'New city, new expenses, same stress',
  effect: 'money_loss',
  effectValue: -400
}, {
  id: 84,
  name: 'Office Politics Cost You 😒',
  category: 'money_loss',
  actionText: 'Someone else got YOUR opportunity',
  effect: 'money_loss',
  effectValue: -150
},
// --- 📚 PATH-SPECIFIC NEGATIVE TILES: HS/GED → Good Job ---
{
  id: 85,
  name: 'Hours Cut This Week 😬',
  category: 'money_loss',
  actionText: 'Schedule changed without warning',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 86,
  name: 'Missed Promotion 😒',
  category: 'money_loss',
  actionText: 'They gave it to someone else...',
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 87,
  name: 'Shift Cut Last Minute 😩',
  category: 'money_loss',
  actionText: 'Manager changed the schedule again',
  effect: 'money_loss',
  effectValue: -120
}, {
  id: 88,
  name: 'Called Out Unpaid 😭',
  category: 'money_loss',
  actionText: "Burnout is real but bills don't care",
  effect: 'money_loss',
  effectValue: -180
}, {
  id: 89,
  name: 'No Raise This Year 😤',
  category: 'money_loss',
  actionText: 'Job feels stuck… again',
  effect: 'money_loss',
  effectValue: -100
},
// --- ⚡ PATH-SPECIFIC NEGATIVE TILES: Dropout → Min Wage ---
{
  id: 90,
  name: 'Shift Got Canceled 😭',
  category: 'money_loss',
  actionText: 'Already got dressed for nothing',
  effect: 'money_loss',
  effectValue: -120
}, {
  id: 91,
  name: 'Hours Cut Again 😬',
  category: 'money_loss',
  actionText: 'Boss keeps cutting your schedule',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 92,
  name: 'Let Go Unexpectedly 💀',
  category: 'money_loss',
  actionText: 'No warning. No severance. Just gone.',
  effect: 'money_loss',
  effectValue: -300
}, {
  id: 93,
  name: 'Missed Work… No Pay 😩',
  category: 'money_loss',
  actionText: "Life happened but the check didn't",
  effect: 'money_loss',
  effectValue: -150
}, {
  id: 94,
  name: 'Transportation Issue 🚗',
  category: 'money_loss',
  actionText: "Couldn't get to work today",
  effect: 'money_loss',
  effectValue: -100
},
// --- 📺 PATH-SPECIFIC NEGATIVE TILES: Self-Employed / Streaming ---
{
  id: 95,
  name: 'Content Flopped 😭',
  category: 'money_loss',
  actionText: 'Put in the work… crickets',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 96,
  name: 'Brand Deal Fell Through 💀',
  category: 'money_loss',
  actionText: 'They ghosted after the contract',
  effect: 'money_loss',
  effectValue: -400
}, {
  id: 97,
  name: 'No Income This Week 😬',
  category: 'money_loss',
  actionText: 'Self-employed means self-stressed',
  effect: 'money_loss',
  effectValue: -300
}, {
  id: 98,
  name: 'Algorithm Not Pushing You 😩',
  category: 'money_loss',
  actionText: 'Your content is invisible',
  effect: 'money_loss',
  effectValue: -250
}, {
  id: 99,
  name: 'Invested Money… No Return 😭',
  category: 'money_loss',
  actionText: 'Bet on yourself and lost this round',
  effect: 'money_loss',
  effectValue: -500
},
// --- NEW MONEY LOSS TILES (104-113) ---
{
  id: 104,
  name: 'Laundry Machine Ate Your Quarters',
  category: 'money_loss',
  actionText: 'And your clothes are still wet',
  effect: 'money_loss',
  effectValue: -15
}, {
  id: 105,
  name: 'Friend Needed Emergency Cash',
  category: 'money_loss',
  actionText: 'You know you are never seeing this again',
  effect: 'money_loss',
  effectValue: -200
}, {
  id: 106,
  name: 'Accidentally Tipped 30%',
  category: 'money_loss',
  actionText: 'Math is hard under pressure',
  effect: 'money_loss',
  effectValue: -25
}, {
  id: 107,
  name: 'Streaming Service Price Hike',
  category: 'money_loss',
  actionText: 'They got you again',
  effect: 'money_loss',
  effectValue: -18
}, {
  id: 108,
  name: 'Lost Your Wallet 😭',
  category: 'money_loss',
  actionText: 'The replacement fees hurt the most',
  effect: 'money_loss',
  effectValue: -350
}, {
  id: 109,
  name: 'HOA Fee Hit',
  category: 'money_loss',
  actionText: 'Paying to be told what to do',
  effect: 'money_loss',
  effectValue: -250
}, {
  id: 110,
  name: 'Gym Membership You Never Use',
  category: 'money_loss',
  actionText: 'Maybe next month...',
  effect: 'money_loss',
  effectValue: -45
}, {
  id: 111,
  name: 'Food Delivery Fee Shock',
  category: 'money_loss',
  actionText: 'The fees cost more than the food',
  effect: 'money_loss',
  effectValue: -22
}, {
  id: 112,
  name: 'Accidentally Left Lights On',
  category: 'money_loss',
  actionText: 'Your dad was right',
  effect: 'money_loss',
  effectValue: -40
}, {
  id: 113,
  name: 'Kid Needed School Supplies',
  category: 'money_loss',
  actionText: 'Why are markers so expensive?',
  effect: 'money_loss',
  effectValue: -80
},
// --- NEW MONEY GAIN TILES (114-123) ---
{
  id: 114,
  name: 'Found a Side Gig 💪',
  category: 'money',
  actionText: 'Hustle paying off',
  effect: 'money_gain',
  effectValue: 300
}, {
  id: 115,
  name: 'Got a Raise!',
  category: 'money',
  actionText: 'Finally some recognition',
  effect: 'money_gain',
  effectValue: 200
}, {
  id: 116,
  name: 'Scratch Ticket Win 🎰',
  category: 'money',
  actionText: "Don't spend it all at once",
  effect: 'money_gain',
  effectValue: 150
}, {
  id: 117,
  name: 'Birthday Money 🎂',
  category: 'money',
  actionText: 'Thanks Grandma!',
  effect: 'money_gain',
  effectValue: 100
}, {
  id: 118,
  name: 'Sold Old Clothes Online',
  category: 'money',
  actionText: 'Decluttering for profit',
  effect: 'money_gain',
  effectValue: 80
}, {
  id: 119,
  name: 'Class Action Settlement Hit',
  category: 'money',
  actionText: 'You forgot you even signed up',
  effect: 'money_gain',
  effectValue: 450
}, {
  id: 120,
  name: 'Neighbor Paid You to Pet Sit',
  category: 'money',
  actionText: 'Getting paid to hang with dogs',
  effect: 'money_gain',
  effectValue: 60
}, {
  id: 121,
  name: 'Won a Bet 😏',
  category: 'money',
  actionText: 'You knew you were right',
  effect: 'money_gain',
  effectValue: 175
}, {
  id: 122,
  name: 'Overtime Approved!',
  category: 'money',
  actionText: 'Tired but richer',
  effect: 'money_gain',
  effectValue: 250
}, {
  id: 123,
  name: 'Random Venmo From Mom 💕',
  category: 'money',
  actionText: 'Moms are the best',
  effect: 'money_gain',
  effectValue: 125
},
// --- NEW LIFE EVENT TILES (124-133) ---
{
  id: 124,
  name: 'Best Friend Moved Away',
  category: 'heartbreak',
  actionText: 'Skip a turn to cry',
  effect: 'skip'
}, {
  id: 125,
  name: 'Got a Compliment From a Stranger',
  category: 'blessing',
  actionText: 'Move ahead 1 space',
  effect: 'move',
  effectValue: 1
}, {
  id: 126,
  name: 'Therapy Breakthrough 🧠',
  category: 'glowup',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 127,
  name: 'Roommate Drama 😤',
  category: 'chaos',
  actionText: 'Move back 1 space',
  effect: 'move',
  effectValue: -1
}, {
  id: 128,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Truth or Dare',
  effect: 'none'
}, {
  id: 129,
  name: 'Started a Journal ✨',
  category: 'glowup',
  actionText: 'Roll again!',
  effect: 'roll_again'
}, {
  id: 130,
  name: 'Family Drama at Dinner',
  category: 'heartbreak',
  actionText: 'Skip a turn',
  effect: 'skip'
}, {
  id: 131,
  name: 'Random Act of Kindness',
  category: 'blessing',
  actionText: 'Move ahead 2 spaces',
  effect: 'move',
  effectValue: 2
}, {
  id: 132,
  name: 'Phone Died at the Worst Time',
  category: 'chaos',
  actionText: 'Move back 1 space',
  effect: 'move',
  effectValue: -1
}, {
  id: 133,
  name: 'WILD CARD',
  category: 'wildcard',
  actionText: 'Spin the Wheel',
  effect: 'none'
}];
// --- REALM EVENTS ---
const CHAOS_REALM_TILE_POOL = [
// Entry tile
{
  id: 0,
  name: 'Welcome to the Chaos Realm',
  emoji: '🔥',
  description: "You've entered a place where everything can fall apart...",
  effect: 'safe_passage' as const,
  effectValue: 0
},
// Chaos/Struggle tiles (converted from CHAOS_REALM_EVENTS + DEEP_STRUGGLE_EVENTS)
{
  id: 1,
  name: 'Lost your job unexpectedly',
  emoji: '😭',
  description: 'Everything just fell apart',
  effect: 'money_loss' as const,
  effectValue: -500
}, {
  id: 2,
  name: 'Car broke down AND you need repairs',
  emoji: '🚗',
  description: 'When it rains, it pours',
  effect: 'money_loss' as const,
  effectValue: -400
}, {
  id: 3,
  name: "Rent due and you're short",
  emoji: '💸',
  description: 'The stress is overwhelming',
  effect: 'money_loss' as const,
  effectValue: -350
}, {
  id: 4,
  name: 'Dental emergency',
  emoji: '😬',
  description: 'Pain and bills at the same time',
  effect: 'money_loss' as const,
  effectValue: -600
}, {
  id: 5,
  name: 'Got sick, no income for days',
  emoji: '🤒',
  description: 'Your body gave out',
  effect: 'money_loss' as const,
  effectValue: -200
}, {
  id: 6,
  name: 'Everything is due at once',
  emoji: '😵‍💫',
  description: 'Drowning in obligations',
  effect: 'money_loss' as const,
  effectValue: -500
}, {
  id: 7,
  name: "Burnout hit hard… can't function",
  emoji: '😩',
  description: 'You need to step away',
  effect: 'skip_turn' as const,
  effectValue: 0
}, {
  id: 8,
  name: 'Life feels overwhelming right now',
  emoji: '💔',
  description: 'Everything is too much',
  effect: 'money_loss' as const,
  effectValue: -250
}, {
  id: 9,
  name: 'Eviction notice showed up',
  emoji: '😭',
  description: 'Nowhere to go',
  effect: 'money_loss' as const,
  effectValue: -700
}, {
  id: 10,
  name: 'Phone got stolen',
  emoji: '📱',
  description: 'Your lifeline is gone',
  effect: 'money_loss' as const,
  effectValue: -400
}, {
  id: 11,
  name: 'Had to borrow money from family',
  emoji: '💔',
  description: 'The shame is real',
  effect: 'money_loss' as const,
  effectValue: -300
}, {
  id: 12,
  name: 'Anxiety spiral hit hard',
  emoji: '😵‍💫',
  description: "Can't catch your breath",
  effect: 'move_back' as const,
  effectValue: -2
}, {
  id: 13,
  name: 'Lost a close friendship',
  emoji: '💔',
  description: 'Alone when you need people most',
  effect: 'money_loss' as const,
  effectValue: -150
}, {
  id: 14,
  name: 'Work drama got out of control',
  emoji: '😤',
  description: 'Everything is falling apart',
  effect: 'money_loss' as const,
  effectValue: -350
},
// Deep struggle tiles
{
  id: 15,
  name: 'Hit a breaking point… need to step away',
  emoji: '🧠',
  description: 'Your mind needs rest',
  effect: 'money_loss' as const,
  effectValue: -400
}, {
  id: 16,
  name: 'Checked into care/support to reset',
  emoji: '🏥',
  description: 'Taking time to heal',
  effect: 'money_loss' as const,
  effectValue: -500
}, {
  id: 17,
  name: 'Coping habits got out of control… time to face it',
  emoji: '😬',
  description: 'Reality check moment',
  effect: 'money_loss' as const,
  effectValue: -300
}, {
  id: 18,
  name: 'Rock bottom… but still breathing',
  emoji: '💪',
  description: 'This is the lowest point',
  effect: 'money_loss' as const,
  effectValue: -600
}, {
  id: 19,
  name: 'Had to start completely over',
  emoji: '😭',
  description: 'Everything you built is gone',
  effect: 'money_loss' as const,
  effectValue: -500
}, {
  id: 20,
  name: 'Lost everything but yourself',
  emoji: '💜',
  description: 'At least you still have you',
  effect: 'money_loss' as const,
  effectValue: -450
},
// THE INSTANT LOSE TILE
{
  id: 21,
  name: 'Rock Bottom — You Lost Everything',
  emoji: '💀',
  description: 'Sometimes the chaos wins...',
  effect: 'instant_lose' as const,
  effectValue: 0
},
// Recovery tiles (converted from RECOVERY_EVENTS)
{
  id: 22,
  name: 'Took time to rest and reset',
  emoji: '🛌',
  description: 'Small steps forward',
  effect: 'recovery' as const,
  effectValue: 100
}, {
  id: 23,
  name: 'Support system showed up for you',
  emoji: '💕',
  description: "You're not alone",
  effect: 'recovery' as const,
  effectValue: 150
}, {
  id: 24,
  name: 'Started getting back on track slowly',
  emoji: '🙌',
  description: 'Progress, not perfection',
  effect: 'recovery' as const,
  effectValue: 200
}, {
  id: 25,
  name: 'Small win… but it meant everything',
  emoji: '💖',
  description: 'Hope is returning',
  effect: 'recovery' as const,
  effectValue: 120
}, {
  id: 26,
  name: 'Found a little peace again',
  emoji: '🕊️',
  description: 'Breathing easier now',
  effect: 'recovery' as const,
  effectValue: 100
}, {
  id: 27,
  name: 'Rebuilding, one step at a time',
  emoji: '✨',
  description: 'Slow and steady',
  effect: 'recovery' as const,
  effectValue: 180
}, {
  id: 28,
  name: 'Someone believed in you when you didnt',
  emoji: '💖',
  description: 'That made all the difference',
  effect: 'recovery' as const,
  effectValue: 200
}, {
  id: 29,
  name: 'Got approved for assistance',
  emoji: '🙌',
  description: 'Help arrived',
  effect: 'recovery' as const,
  effectValue: 250
}, {
  id: 30,
  name: 'Community rallied around you',
  emoji: '💕',
  description: 'People showed up',
  effect: 'recovery' as const,
  effectValue: 180
}, {
  id: 31,
  name: 'Woke up and chose healing',
  emoji: '✨',
  description: 'Today is a new day',
  effect: 'recovery' as const,
  effectValue: 150
},
// Safe passage tiles (breathing room)
{
  id: 32,
  name: 'A moment of calm',
  emoji: '🌙',
  description: 'Nothing bad happened today',
  effect: 'safe_passage' as const,
  effectValue: 0
}, {
  id: 33,
  name: 'Caught your breath',
  emoji: '😮‍💨',
  description: 'Just a moment to breathe',
  effect: 'safe_passage' as const,
  effectValue: 0
}, {
  id: 34,
  name: 'Survived another day',
  emoji: '💪',
  description: 'That counts for something',
  effect: 'safe_passage' as const,
  effectValue: 0
},
// Exit tile
{
  id: 35,
  name: 'You Made It Through',
  emoji: '🌅',
  description: 'The chaos realm releases you... for now',
  effect: 'exit' as const,
  effectValue: 0
}];
const CAREER_SWITCH_EVENTS = [{
  name: 'Took time off to figure things out 😬',
  amount: -300
}, {
  name: 'Invested in new skills/courses 📚',
  amount: -400
}, {
  name: 'Lost stable income during transition 💸',
  amount: -500
}, {
  name: 'Bought equipment/supplies 🛠️',
  amount: -350
}, {
  name: 'Took on debt to make the switch 💳',
  amount: -600
}, {
  name: 'Income unstable for a while 😩',
  amount: -250
}, {
  name: 'Started from scratch 😭',
  amount: -450
}];
// --- 🚗 UNIVERSAL TRANSPORTATION EVENTS ---
const TRANSPORTATION_EVENTS = [
// Car / Gas
{
  name: 'Low on gas… fill up ⛽',
  amount: -60,
  emoji: '⛽'
}, {
  name: 'Gas prices hit again 😒',
  amount: -80,
  emoji: '⛽'
}, {
  name: 'Oil change due 🛠️',
  amount: -75,
  emoji: '🛠️'
}, {
  name: 'Tire pressure issue… quick fix',
  amount: -40,
  emoji: '🔧'
},
// Electric Vehicle
{
  name: 'Battery low… need to charge 🔌',
  amount: -30,
  emoji: '🔌'
}, {
  name: 'Charging station cost more than expected 😬',
  amount: -50,
  emoji: '🔌'
},
// Uber / Rideshare
{
  name: 'Took an Uber… surge pricing 😭',
  amount: -45,
  emoji: '🚕'
}, {
  name: 'Late night ride cost more than expected 🚕',
  amount: -60,
  emoji: '🚕'
}, {
  name: 'Had to Uber instead of driving',
  amount: -35,
  emoji: '🚕'
},
// Public Transport
{
  name: 'Bus/train fare 🚌',
  amount: -10,
  emoji: '🚌'
}, {
  name: 'Had to reload transit card',
  amount: -25,
  emoji: '🚌'
}, {
  name: 'Missed ride… paid again 😒',
  amount: -15,
  emoji: '🚌'
}, {
  name: 'Car insurance went up 😤',
  amount: -90,
  emoji: '🚗'
}, {
  name: 'Parking garage fee 🅿️',
  amount: -35,
  emoji: '🅿️'
}, {
  name: 'Car registration renewal 📋',
  amount: -120,
  emoji: '📋'
}, {
  name: 'Windshield wiper replacement',
  amount: -30,
  emoji: '🚗'
}, {
  name: 'Uber pool still expensive 😒',
  amount: -28,
  emoji: '🚕'
}, {
  name: 'Train delay made you late',
  amount: -20,
  emoji: '🚌'
}, {
  name: 'Toll road surprise 💸',
  amount: -15,
  emoji: '🛣️'
}, {
  name: 'Had to jump start the car 🔋',
  amount: -50,
  emoji: '🔋'
}];
// --- 4 LIFE PATHS ---
const LIFE_PATHS = [{
  name: 'College → Best Job',
  emoji: '🎓',
  icon: GraduationCapIcon,
  color: 'from-violet-400 to-purple-500',
  glowColor: 'rgba(139,92,246,0.5)',
  textColor: 'text-violet-600',
  bgLight: 'bg-violet-50',
  borderColor: 'border-violet-300'
}, {
  name: 'HS / GED → Good Job',
  emoji: '📚',
  icon: BookOpenIcon,
  color: 'from-pink-400 to-rose-500',
  glowColor: 'rgba(236,72,153,0.5)',
  textColor: 'text-pink-600',
  bgLight: 'bg-pink-50',
  borderColor: 'border-pink-300'
}, {
  name: 'Drop Out → Min Wage',
  emoji: '⚡',
  icon: ZapIcon,
  color: 'from-orange-400 to-coral-500',
  glowColor: 'rgba(251,146,60,0.5)',
  textColor: 'text-orange-600',
  bgLight: 'bg-orange-50',
  borderColor: 'border-orange-300'
}, {
  name: 'Self Employed → Streaming',
  emoji: '📺',
  icon: TvIcon,
  color: 'from-teal-400 to-cyan-500',
  glowColor: 'rgba(20,184,166,0.5)',
  textColor: 'text-teal-600',
  bgLight: 'bg-teal-50',
  borderColor: 'border-teal-300'
}];
// --- PATH-SPECIFIC JOB POOLS ---
const PATH_JOBS: Record<number, string[]> = {
  0: ['corporate-remote', 'nurse', 'engineer', 'accountant', 'marketing-mgr', 'hr-specialist', 'software-dev', 'project-mgr', 'teacher', 'office-mgr', 'financial-analyst', 'healthcare-admin', 'it-specialist'],
  1: ['call-center', 'retail-mgr', 'warehouse-supervisor', 'delivery-driver', 'admin-assistant', 'bank-teller', 'customer-service', 'security-guard', 'medical-assistant', 'receptionist', 'shift-supervisor', 'factory-worker'],
  2: ['fast-food', 'retail-associate', 'house-cleaner', 'dishwasher', 'stocker', 'day-labor', 'gas-station', 'lawn-care', 'warehouse-worker', 'babysitting', 'thrift-flipper'],
  3: ['streamer', 'content-creator', 'freelancer', 'small-biz', 'online-seller', 'dropshipper', 'social-media-mgr', 'photographer', 'hair-stylist', 'nail-tech', 'personal-trainer', 'coach-consultant', 'gig-driver']
};
const getJobsForPath = (pathIndex: number): Job[] => {
  const jobIds = PATH_JOBS[pathIndex] || [];
  return ALL_JOBS.filter(j => jobIds.includes(j.id));
};
// Map tiles to 4 paths — original tiles + money struggle tiles scattered in each
// Money struggle tiles (30-57) are distributed evenly and mixed into early/mid/late positions
const DEFAULT_PATH_TILE_ASSIGNMENTS = [
// Path 0: College → Best Job (+ path-specific: 80-84)
[0, 1, 30, 58, 80, 35, 3, 60, 40, 81, 4, 62, 48, 100, 5, 82, 66, 44, 6, 67, 83, 52, 7, 75, 84, 55, 29],
// Path 1: HS/GED → Good Job (+ path-specific: 85-89)
[0, 59, 31, 85, 9, 36, 61, 41, 86, 12, 63, 49, 102, 68, 87, 45, 14, 69, 88, 53, 15, 76, 89, 56, 29],
// Path 2: Drop Out → Min Wage (+ path-specific: 90-94)
[0, 16, 32, 90, 64, 17, 37, 91, 70, 18, 42, 71, 92, 50, 101, 21, 65, 93, 46, 22, 72, 54, 94, 23, 77, 57, 29],
// Path 3: Self Employed → Streaming (+ path-specific: 95-99)
[0, 24, 33, 95, 73, 38, 26, 96, 74, 43, 27, 97, 78, 51, 103, 34, 98, 79, 47, 99, 39, 28, 29]];
const getTileById = (id: number): TileData => {
  return TILES.find(t => t.id === id) || TILES[0];
};
// Position calculator for the 4-path linear layout
const getBoardPosition = (tileId: number, activePathTiles: number[][] = DEFAULT_PATH_TILE_ASSIGNMENTS): {
  x: number;
  y: number;
} => {
  // Start tile — bottom center
  if (tileId === 0) return {
    x: 50,
    y: 98
  };
  // Finish tile — top center
  if (tileId === 29) return {
    x: 50,
    y: 2
  };
  // Tax tiles — positioned in center
  if (tileId === 10) return {
    x: 50,
    y: 70
  };
  if (tileId === 20) return {
    x: 50,
    y: 40
  };
  if (tileId === 28) return {
    x: 50,
    y: 15
  };
  // Find which path this tile belongs to
  for (let pathIdx = 0; pathIdx < activePathTiles.length; pathIdx++) {
    const pathTiles = activePathTiles[pathIdx];
    const posInPath = pathTiles.indexOf(tileId);
    if (posInPath !== -1) {
      const progress = posInPath / (pathTiles.length - 1);
      // 4 paths: vertical lanes
      const xPositions = [15, 38, 62, 85];
      // Y goes from 95 (bottom) to 5 (top)
      const y = 95 - progress * 90;
      // Add slight wobble to make it feel organic
      const wobble = Math.sin(posInPath * 2) * 2;
      return {
        x: xPositions[pathIdx] + wobble,
        y: y
      };
    }
  }
  // Fallback for any unmapped tiles
  return {
    x: 50,
    y: 50
  };
};
// Floating particles component
function FloatingParticles({
  count = 30
}: {
  count?: number;
}) {
  return <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-1657">
      {Array.from({
      length: count
    }).map((_, i) => <motion.div key={i} className="absolute rounded-full" style={{
      width: 2 + Math.random() * 4,
      height: 2 + Math.random() * 4,
      background: ['rgba(236,72,153,0.3)', 'rgba(168,85,247,0.3)', 'rgba(96,165,250,0.3)', 'rgba(251,191,36,0.3)', 'rgba(255,255,255,0.5)'][i % 5],
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      y: [0, -30 - Math.random() * 40, 0],
      x: [0, (Math.random() - 0.5) * 20, 0],
      opacity: [0.2, 0.6, 0.2],
      scale: [1, 1.3, 1]
    }} transition={{
      duration: 4 + Math.random() * 6,
      repeat: Infinity,
      delay: Math.random() * 5,
      ease: 'easeInOut'
    }} data-id="element-1658" />)}
    </div>;
}
// Sparkle burst component for board landing
function SparkleBurst({
  active
}: {
  active: boolean;
}) {
  if (!active) return null;
  return <AnimatePresence data-id="element-1659">
      <div className="absolute inset-0 pointer-events-none z-10" data-id="element-1660">
        {Array.from({
        length: 40
      }).map((_, i) => {
        const angle = i / 40 * Math.PI * 2;
        const distance = 80 + Math.random() * 120;
        return <motion.div key={i} className="absolute left-1/2 top-1/2 rounded-full" style={{
          width: 3 + Math.random() * 5,
          height: 3 + Math.random() * 5,
          background: ['#f9a8d4', '#c4b5fd', '#93c5fd', '#fcd34d', '#ffffff'][i % 5]
        }} initial={{
          x: 0,
          y: 0,
          opacity: 1,
          scale: 1
        }} animate={{
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance - 40,
          opacity: 0,
          scale: 0
        }} transition={{
          duration: 1.2 + Math.random() * 0.8,
          ease: 'easeOut'
        }} data-id="element-1661" />;
      })}
      </div>
    </AnimatePresence>;
}
// --- COMPONENT ---
export function BoardGamePage() {
  const [phase, setPhase] = useState<GamePhase>('setup');
  const [activePathTiles, setActivePathTiles] = useState(DEFAULT_PATH_TILE_ASSIGNMENTS);
  const [players, setPlayers] = useState<Player[]>([{
    id: '1',
    name: 'Player 1',
    color: 'pink',
    position: 0,
    skipNextTurn: false,
    avatar: null,
    job: null,
    money: 0,
    turnsPlayed: 0,
    roundsWithoutIncome: 0,
    evolved: false,
    pathIndex: null,
    inSchool: false,
    schoolTurnsLeft: 0,
    studentLoanDebt: 0,
    chaosTriggersRemaining: 0
  }, {
    id: '2',
    name: 'Player 2',
    color: 'purple',
    position: 0,
    skipNextTurn: false,
    avatar: null,
    job: null,
    money: 0,
    turnsPlayed: 0,
    roundsWithoutIncome: 0,
    evolved: false,
    pathIndex: null,
    inSchool: false,
    schoolTurnsLeft: 0,
    studentLoanDebt: 0,
    chaosTriggersRemaining: 0
  }]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [diceValue, setDiceValue] = useState<number | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [winner, setWinner] = useState<Player | null>(null);
  const [zoomedTile, setZoomedTile] = useState<number | null>(null);
  const [jobRevealIndex, setJobRevealIndex] = useState(-1);
  const [jobSpinning, setJobSpinning] = useState(false);
  const [jobOptions, setJobOptions] = useState<Job[]>([]);
  const [jobSelectingPlayerIndex, setJobSelectingPlayerIndex] = useState<number | null>(null);
  const [taxResults, setTaxResults] = useState<Array<{
    name: string;
    emoji: string;
    taxType: string;
    taxed: number;
    remaining: number;
  }>>([]);
  const [showEvolution, setShowEvolution] = useState(false);
  const [evolvedJob, setEvolvedJob] = useState<Job | null>(null);
  const [jobEffectMessage, setJobEffectMessage] = useState<string | null>(null);
  const [incomeFlash, setIncomeFlash] = useState<string | null>(null);
  const [boardLanded, setBoardLanded] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const [glowingPath, setGlowingPath] = useState(0);
  const boardRef = useRef<HTMLDivElement>(null);
  const [pathChoicePlayerIndex, setPathChoicePlayerIndex] = useState(0);
  // Realm State
  const [realmEvents, setRealmEvents] = useState<Array<{
    name: string;
    amount: number;
  }>>([]);
  const [currentRealmEventIndex, setCurrentRealmEventIndex] = useState(0);
  const [realmTotalLoss, setRealmTotalLoss] = useState(0);
  const [pendingNewPath, setPendingNewPath] = useState<number | null>(null);
  // New Chaos Realm State
  const [chaosRealmTiles, setChaosRealmTiles] = useState<typeof CHAOS_REALM_TILE_POOL>([]);
  const [chaosRealmPosition, setChaosRealmPosition] = useState(0);
  const [chaosRealmDiceValue, setChaosRealmDiceValue] = useState<number | null>(null);
  const [chaosRealmRolling, setChaosRealmRolling] = useState(false);
  const [eliminatedPlayers, setEliminatedPlayers] = useState<string[]>([]);
  const [showElimination, setShowElimination] = useState(false);
  const [eliminatedPlayerName, setEliminatedPlayerName] = useState('');
  const [showChaosTransition, setShowChaosTransition] = useState(false);
  const activeChaosTileRef = useRef<HTMLDivElement>(null);
  // Transportation event state
  const [transportEvent, setTransportEvent] = useState<{
    name: string;
    amount: number;
    emoji: string;
  } | null>(null);
  const generateShuffledPaths = () => {
    // Define pools
    const UNIVERSAL_NEGATIVE_IDS = [...Array.from({
      length: 28
    }, (_, i) => i + 30), ...Array.from({
      length: 10
    }, (_, i) => i + 104) // 104-113
    ];
    const UNIVERSAL_POSITIVE_IDS = [...Array.from({
      length: 22
    }, (_, i) => i + 58), ...Array.from({
      length: 10
    }, (_, i) => i + 114) // 114-123
    ];
    const LIFE_EVENT_IDS = [1, 3, 4, 5, 6, 7, 9, 11, 12, 14, 15, 16, 17, 18, 21, 22, 23, 24, 26, 27, ...Array.from({
      length: 10
    }, (_, i) => i + 124) // 124-133
    ];
    const PATH_SPECIFIC: Record<number, number[]> = {
      0: [80, 81, 82, 83, 84],
      1: [85, 86, 87, 88, 89],
      2: [90, 91, 92, 93, 94],
      3: [95, 96, 97, 98, 99]
    };
    const SPECIAL_TILES: Record<number, number> = {
      0: 100,
      1: 102,
      2: 101,
      3: 103 // career switch
    };
    // Health/food expense tile IDs excluded from Dropout path (Medicaid + food assistance)
    const DROPOUT_EXCLUDED_TILES = [40, 41, 42, 45, 111];
    // Shuffle helper
    const shuffle = (array: number[]) => [...array].sort(() => Math.random() - 0.5);
    let availableNeg = shuffle(UNIVERSAL_NEGATIVE_IDS);
    let availablePos = shuffle(UNIVERSAL_POSITIVE_IDS);
    let availableLife = shuffle(LIFE_EVENT_IDS);
    const newPaths = [];
    for (let i = 0; i < 4; i++) {
      // For Dropout path (index 2), filter out health/food tiles (Medicaid + food assistance)
      let negSubset: number[];
      if (i === 2) {
        const filteredNeg = availableNeg.filter(id => !DROPOUT_EXCLUDED_TILES.includes(id));
        negSubset = filteredNeg.slice(0, 8);
        availableNeg = availableNeg.filter(id => !negSubset.includes(id));
      } else {
        negSubset = availableNeg.splice(0, 8);
      }
      const posSubset = availablePos.splice(0, 6);
      const lifeSubset = availableLife.splice(0, 5);
      // Combine middle tiles and shuffle them
      const middleTiles = shuffle([...negSubset, ...posSubset, ...lifeSubset, ...PATH_SPECIFIC[i], SPECIAL_TILES[i]]);
      // Build path: Start (0) -> Middle -> Finish (29)
      newPaths.push([0, ...middleTiles, 29]);
    }
    setActivePathTiles(newPaths);
  };
  // Cycle path glow
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setGlowingPath(prev => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, [phase]);
  // Board landing animation trigger
  useEffect(() => {
    if (phase === 'playing' && !boardLanded) {
      setTimeout(() => {
        setBoardLanded(true);
        setShowBurst(true);
        setTimeout(() => setShowBurst(false), 2000);
      }, 300);
    }
  }, [phase]);
  // --- SETUP ---
  const addPlayer = () => {
    if (players.length >= 6) return;
    const colors: PlayerColor[] = ['pink', 'purple', 'blue', 'teal', 'gold', 'coral'];
    setPlayers([...players, {
      id: Date.now().toString(),
      name: `Player ${players.length + 1}`,
      color: colors[players.length % 6],
      position: 0,
      skipNextTurn: false,
      avatar: null,
      job: null,
      money: 0,
      turnsPlayed: 0,
      roundsWithoutIncome: 0,
      evolved: false,
      pathIndex: null,
      inSchool: false,
      schoolTurnsLeft: 0,
      studentLoanDebt: 0,
      chaosTriggersRemaining: 0
    }]);
  };
  const removePlayer = (id: string) => {
    if (players.length <= 2) return;
    setPlayers(players.filter(p => p.id !== id));
  };
  const updatePlayer = (id: string, field: keyof Player, value: any) => {
    setPlayers(players.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const handleAvatarUpload = (playerId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      setPlayers(prev => prev.map(p => p.id === playerId ? {
        ...p,
        avatar: e.target?.result as string
      } : p));
    };
    reader.readAsDataURL(file);
  };
  const goToPathChoice = () => {
    generateShuffledPaths();
    setPlayers(players.map(p => ({
      ...p,
      position: 0,
      skipNextTurn: false,
      money: 0,
      turnsPlayed: 0,
      roundsWithoutIncome: 0,
      job: null,
      evolved: false,
      pathIndex: null,
      inSchool: false,
      schoolTurnsLeft: 0,
      studentLoanDebt: 0,
      chaosTriggersRemaining: 0
    })));
    setPhase('path_choice');
    setPathChoicePlayerIndex(0);
  };
  const selectPath = (playerIndex: number, pathIndex: number) => {
    // College path (0): Immediate debt on selection (no school phase)
    const isCollege = pathIndex === 0;
    const loanAmount = isCollege ? Math.floor(Math.random() * 2001) + 1000 : 0; // $1000-$3000
    // Per-path chaos trigger limits
    const chaosLimits: Record<number, number> = {
      0: 3,
      1: 2,
      2: 2,
      3: 1
    };
    setPlayers(prev => prev.map((p, i) => i === playerIndex ? {
      ...p,
      pathIndex,
      inSchool: false,
      schoolTurnsLeft: 0,
      studentLoanDebt: loanAmount,
      money: isCollege ? -loanAmount : 0,
      chaosTriggersRemaining: chaosLimits[pathIndex] ?? 2
    } : p));
    if (playerIndex + 1 < players.length) {
      setPathChoicePlayerIndex(playerIndex + 1);
    } else {
      goToJobAssignment();
    }
  };
  const goToJobAssignment = () => {
    setPhase('job_assignment');
    setJobRevealIndex(-1);
    setJobOptions([]);
    setJobSelectingPlayerIndex(null);
  };
  // --- JOB ASSIGNMENT ---
  const revealNextJob = () => {
    const nextIdx = jobRevealIndex + 1;
    if (nextIdx >= players.length) return;
    setJobSpinning(true);
    setTimeout(() => {
      const player = players[nextIdx];
      const pathIdx = player.pathIndex !== null ? player.pathIndex : 0;
      const jobs = getJobsForPath(pathIdx);
      const shuffled = [...jobs].sort(() => Math.random() - 0.5);
      const numOptions = Math.floor(Math.random() * 2) + 2; // 2 to 3 options
      setJobOptions(shuffled.slice(0, numOptions));
      setJobSelectingPlayerIndex(nextIdx);
      setJobSpinning(false);
      setJobRevealIndex(nextIdx);
    }, 2000);
  };
  const selectJob = (job: Job) => {
    if (jobSelectingPlayerIndex === null) return;
    setPlayers(prev => prev.map((p, i) => i === jobSelectingPlayerIndex ? {
      ...p,
      job: job
    } : p));
    setJobOptions([]);
    setJobSelectingPlayerIndex(null);
  };
  const startPlaying = () => {
    setPhase('journey_start');
  };
  const startActualPlaying = () => {
    setBoardLanded(false);
    setPhase('playing');
    setCurrentPlayerIndex(0);
    setDiceValue(null);
    setShowPopup(false);
    setZoomedTile(null);
  };
  // --- INCOME ---
  const earnIncome = (playerIndex: number): number => {
    const player = players[playerIndex];
    if (!player.job) return 0;
    const job = player.job;
    // Self-employed path (Path 3) jobs have variable income
    if (player.pathIndex === 3) {
      // Randomize between 30% and 180% of base wage
      const multiplier = 0.3 + Math.random() * 1.5;
      const variableWage = Math.max(0, Math.floor(job.wage * multiplier));
      return variableWage * 8;
    }
    return job.wage * 8;
  };
  // --- JOB EFFECTS ---
  const applyJobPreRollEffect = (playerIndex: number): boolean => {
    const player = players[playerIndex];
    if (!player.job) return false;
    const job = player.job;
    const skipAndNext = (msg: string) => {
      setJobEffectMessage(msg);
      setPlayers(prev => prev.map((p, i) => i === playerIndex ? {
        ...p,
        skipNextTurn: true
      } : p));
      setTimeout(() => {
        setJobEffectMessage(null);
        setCurrentPlayerIndex((playerIndex + 1) % players.length);
      }, 2500);
      return true;
    };
    // Path 0: College
    if (job.id === 'nurse' && player.turnsPlayed > 0 && player.turnsPlayed % 3 === 0) return skipAndNext(`🧑‍⚕️ ${player.name}: Burnout hit! Skip this turn.`);
    if (job.id === 'corporate-remote' && Math.random() < 0.2) return skipAndNext(`💻 ${player.name}: Forgot to log in! Skip this turn.`);
    // Path 2: Dropout
    if (job.id === 'fast-food' && Math.random() < 0.2) return skipAndNext(`🍔 ${player.name}: Lunch rush meltdown! Skip this turn.`);
    // Path 1: HS/GED
    if (job.id === 'warehouse-supervisor' && Math.random() < 0.2) return skipAndNext(`📦 ${player.name}: Overworked! Skip this turn.`);
    return false;
  };
  // --- ENTREPRENEUR EVOLUTION ---
  const checkEvolution = (playerIndex: number) => {
    // Evolution system removed as per new job pools
    return;
  };
  // --- GAMEPLAY ---
  const rollDice = () => {
    if (isRolling || showPopup || showEvolution || jobEffectMessage || transportEvent) return;
    // Transportation event check (~17% chance per turn)
    if (Math.random() < 0.17) {
      const event = TRANSPORTATION_EVENTS[Math.floor(Math.random() * TRANSPORTATION_EVENTS.length)];
      setTransportEvent(event);
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        money: p.money + event.amount
      } : p));
      return;
    }
    executeRoll();
  };
  const dismissTransportEvent = () => {
    setTransportEvent(null);
    executeRoll();
  };
  const executeRoll = () => {
    const player = players[currentPlayerIndex];
    if (!player) return;
    const income = earnIncome(currentPlayerIndex);
    // --- COLLEGE POST-GRADUATION: Student loan payment (~35% chance per turn) ---
    let loanPayment = 0;
    if (player.pathIndex === 0 && player.studentLoanDebt > 0 && Math.random() < 0.35) {
      loanPayment = -(Math.floor(Math.random() * 151) + 150); // -$150 to -$300
    }
    // Lucky Break check for Self-Employed / Streaming path (~15% chance)
    const isLuckyBreakJob = player.pathIndex === 3;
    const luckyBreakTriggered = isLuckyBreakJob && Math.random() < 0.15;
    const luckyBreakAmount = luckyBreakTriggered ? Math.floor(Math.random() * 600) + 200 // $200 - $800
    : 0;
    // College bonus check (~20% chance, +$200-$600)
    const collegeBonusTriggered = player.pathIndex === 0 && Math.random() < 0.2;
    const collegeBonusAmount = collegeBonusTriggered ? Math.floor(Math.random() * 401) + 200 // $200 - $600
    : 0;
    const totalGain = income + luckyBreakAmount + collegeBonusAmount + loanPayment;
    if (totalGain !== 0 || income > 0) {
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        money: p.money + totalGain,
        turnsPlayed: p.turnsPlayed + 1
      } : p));
      // Build income flash message
      let flashParts: string[] = [];
      if (income > 0) flashParts.push(`+${income}`);
      if (collegeBonusTriggered) flashParts.push(`🎉 BONUS +${collegeBonusAmount}`);
      if (luckyBreakTriggered) flashParts.push(`🍀 LUCKY BREAK +${luckyBreakAmount}`);
      if (loanPayment < 0) flashParts.push(`💳 Loan ${loanPayment}`);
      setIncomeFlash(flashParts.join(' | '));
      const flashDuration = luckyBreakTriggered || collegeBonusTriggered || loanPayment < 0 ? 2500 : 1500;
      setTimeout(() => setIncomeFlash(null), flashDuration);
    } else {
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        turnsPlayed: p.turnsPlayed + 1
      } : p));
    }
    checkEvolution(currentPlayerIndex);
    if (showEvolution) return;
    doNormalRoll();
  };
  const doNormalRoll = () => {
    setIsRolling(true);
    setDiceValue(null);
    setTimeout(() => {
      const roll = Math.floor(Math.random() * 6) + 1;
      setDiceValue(roll);
      setIsRolling(false);
      setTimeout(() => movePlayer(roll), 1000);
    }, 1500);
  };
  const movePlayer = (spaces: number) => {
    const player = players[currentPlayerIndex];
    if (!player) return;
    const pathIdx = player.pathIndex !== null ? player.pathIndex : 0;
    const pathLength = activePathTiles[pathIdx].length;
    let newPosIndex = player.position + spaces;
    if (newPosIndex >= pathLength) newPosIndex = pathLength - 1;
    if (newPosIndex < 0) newPosIndex = 0;
    const finalPos = newPosIndex;
    setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
      ...p,
      position: finalPos
    } : p));
    setTimeout(() => handleLandOnTile(finalPos, {
      ...player,
      position: finalPos
    }), 1000);
  };
  const handleLandOnTile = (positionIndex: number, player: Player) => {
    const pathIdx = player.pathIndex !== null ? player.pathIndex : 0;
    const tileId = activePathTiles[pathIdx][positionIndex];
    const tile = getTileById(tileId);
    if (tile.effect === 'tax') {
      const results = players.map(p => {
        if (!p.job) return {
          name: p.name,
          emoji: '❓',
          taxType: 'N/A',
          taxed: 0,
          remaining: p.money
        };
        const taxAmount = Math.floor(p.money * p.job.taxRate);
        return {
          name: p.name,
          emoji: p.job.emoji,
          taxType: p.job.taxType,
          taxed: taxAmount,
          remaining: p.money - taxAmount
        };
      });
      setTaxResults(results);
      setPlayers(prev => prev.map((p, i) => ({
        ...p,
        money: results[i].remaining
      })));
      setZoomedTile(tileId);
      setPhase('tax_event');
      return;
    }
    // Check for Chaos Realm trigger (uses per-path chaos trigger limits)
    const isNormalTile = tile.category !== 'start' && tile.category !== 'finish' && tile.category !== 'tax' && tile.effect !== 'career_switch' && tile.effect !== 'chaos_portal';
    const currentPlayer = players[currentPlayerIndex];
    const hasTriggersLeft = currentPlayer && currentPlayer.chaosTriggersRemaining > 0;
    const randomChaosTrigger = isNormalTile && hasTriggersLeft && Math.random() < 0.1;
    if (tile.effect === 'chaos_portal' || randomChaosTrigger) {
      // Decrement chaos triggers remaining
      if (randomChaosTrigger) {
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          chaosTriggersRemaining: p.chaosTriggersRemaining - 1
        } : p));
      }
      // Generate Chaos Realm Path
      const pathLength = Math.floor(Math.random() * 6) + 10; // 10 to 15 tiles
      const newChaosTiles = [];
      // 1. Always start with Entry Tile
      newChaosTiles.push(CHAOS_REALM_TILE_POOL[0]);
      // 2. Generate middle tiles
      const middleTilesCount = pathLength - 2; // -1 for entry, -1 for exit
      // Get pools
      const strugglePool = CHAOS_REALM_TILE_POOL.filter(t => t.id >= 1 && t.id <= 20);
      const recoveryPool = CHAOS_REALM_TILE_POOL.filter(t => t.id >= 22 && t.id <= 31);
      const safePool = CHAOS_REALM_TILE_POOL.filter(t => t.id >= 32 && t.id <= 34);
      const instantLoseTile = CHAOS_REALM_TILE_POOL.find(t => t.id === 21)!;
      // Shuffle pools
      const shuffle = (arr: any[]) => [...arr].sort(() => Math.random() - 0.5);
      const shuffledStruggle = shuffle(strugglePool);
      const shuffledRecovery = shuffle(recoveryPool);
      const shuffledSafe = shuffle(safePool);
      // Build middle section: mostly struggle, 1-2 recovery, 1-2 safe
      const middleSection = [];
      const numRecovery = Math.floor(Math.random() * 2) + 1; // 1-2
      const numSafe = Math.floor(Math.random() * 2) + 1; // 1-2
      const numStruggle = middleTilesCount - numRecovery - numSafe - 1; // -1 for instant lose
      middleSection.push(...shuffledStruggle.slice(0, numStruggle));
      middleSection.push(...shuffledRecovery.slice(0, numRecovery));
      middleSection.push(...shuffledSafe.slice(0, numSafe));
      // Add instant lose tile
      middleSection.push(instantLoseTile);
      // Shuffle middle section so instant lose is random (but not first or last)
      const shuffledMiddle = shuffle(middleSection);
      newChaosTiles.push(...shuffledMiddle);
      // 3. Always end with Exit Tile
      newChaosTiles.push(CHAOS_REALM_TILE_POOL.find(t => t.id === 35)!);
      setChaosRealmTiles(newChaosTiles);
      setChaosRealmPosition(0);
      setRealmTotalLoss(0);
      setShowChaosTransition(true);
      setPhase('chaos_realm');
      setZoomedTile(tileId);
      setTimeout(() => {
        setShowChaosTransition(false);
      }, 2500);
      return;
    }
    if (tile.effect === 'career_switch') {
      const numEvents = Math.floor(Math.random() * 3) + 2; // 2 to 4 events
      const shuffledEvents = [...CAREER_SWITCH_EVENTS].sort(() => Math.random() - 0.5).slice(0, numEvents);
      setRealmEvents(shuffledEvents);
      setCurrentRealmEventIndex(0);
      setRealmTotalLoss(0);
      setPendingNewPath(null);
      setPhase('career_switch');
      setZoomedTile(tileId);
      return;
    }
    setZoomedTile(tileId);
    setShowPopup(true);
    const pathLength = activePathTiles[pathIdx].length;
    if (positionIndex === pathLength - 1) {
      setTimeout(() => {
        setWinner(player);
        setPhase('winner');
      }, 2000);
    }
  };
  // --- REALM LOGIC ---
  const advanceRealmEvent = () => {
    if (phase === 'career_switch') {
      const currentEvent = realmEvents[currentRealmEventIndex];
      setRealmTotalLoss(prev => prev + currentEvent.amount);
      if (currentRealmEventIndex < realmEvents.length - 1) {
        setCurrentRealmEventIndex(prev => prev + 1);
      } else {
        setCurrentRealmEventIndex(realmEvents.length);
      }
    }
  };
  const rollChaosDice = () => {
    if (chaosRealmRolling || showElimination) return;
    setChaosRealmRolling(true);
    setChaosRealmDiceValue(null);
    setTimeout(() => {
      // Roll 1-3 for chaos realm
      const roll = Math.floor(Math.random() * 3) + 1;
      setChaosRealmDiceValue(roll);
      setChaosRealmRolling(false);
      setTimeout(() => {
        let newPos = chaosRealmPosition + roll;
        if (newPos >= chaosRealmTiles.length - 1) {
          newPos = chaosRealmTiles.length - 1;
        }
        setChaosRealmPosition(newPos);
        setTimeout(() => handleChaosTileEffect(newPos), 1000);
      }, 1000);
    }, 1500);
  };
  const handleChaosTileEffect = (pos: number) => {
    const tile = chaosRealmTiles[pos];
    if (!tile) return;
    if (tile.effect === 'instant_lose') {
      const player = players[currentPlayerIndex];
      setEliminatedPlayerName(player.name);
      setShowElimination(true);
      const newEliminated = [...eliminatedPlayers, player.id];
      setEliminatedPlayers(newEliminated);
      setTimeout(() => {
        setShowElimination(false);
        // Check if only 1 player remains
        const remainingPlayers = players.filter(p => !newEliminated.includes(p.id));
        if (remainingPlayers.length === 1) {
          setWinner(remainingPlayers[0]);
          setPhase('winner');
          return;
        }
        setPhase('playing');
        setZoomedTile(null);
        setChaosRealmDiceValue(null);
        advanceToNextPlayer(newEliminated);
      }, 4000);
      return;
    }
    if (tile.effect === 'money_loss' || tile.effect === 'recovery') {
      setRealmTotalLoss(prev => prev + tile.effectValue);
    } else if (tile.effect === 'move_back') {
      setTimeout(() => {
        let backPos = pos + tile.effectValue;
        if (backPos < 0) backPos = 0;
        setChaosRealmPosition(backPos);
        // Don't trigger the effect of the tile we moved back to, to avoid infinite loops
      }, 1500);
    } else if (tile.effect === 'skip_turn') {
      setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
        ...p,
        skipNextTurn: true
      } : p));
    } else if (tile.effect === 'exit') {
      setTimeout(() => {
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          money: p.money + realmTotalLoss
        } : p));
        setPhase('playing');
        setZoomedTile(null);
        setChaosRealmDiceValue(null);
        advanceToNextPlayer();
      }, 2000);
    }
  };
  const advanceToNextPlayer = (customEliminated?: string[]) => {
    const eliminated = customEliminated || eliminatedPlayers;
    setCurrentPlayerIndex(prevIdx => {
      let nextIdx = (prevIdx + 1) % players.length;
      // Skip eliminated players
      while (eliminated.includes(players[nextIdx].id) && eliminated.length < players.length - 1) {
        nextIdx = (nextIdx + 1) % players.length;
      }
      return nextIdx;
    });
  };
  const handleCareerSwitchSelection = (pathIndex: number) => {
    setPendingNewPath(pathIndex);
  };
  const confirmCareerSwitch = () => {
    if (pendingNewPath === null) return;
    const newJobs = getJobsForPath(pendingNewPath);
    const randomJob = newJobs[Math.floor(Math.random() * newJobs.length)];
    setPlayers(prev => prev.map((p, i) => {
      if (i === currentPlayerIndex) {
        const oldPathLength = activePathTiles[p.pathIndex || 0].length;
        const newPathLength = activePathTiles[pendingNewPath].length;
        const progress = p.position / Math.max(1, oldPathLength - 1);
        const newPos = Math.floor(progress * (newPathLength - 1));
        return {
          ...p,
          money: p.money + realmTotalLoss,
          pathIndex: pendingNewPath,
          job: randomJob,
          position: newPos
        };
      }
      return p;
    }));
    setPhase('playing');
    setZoomedTile(null);
    advanceToNextPlayer();
  };
  const applyEffectAndNextTurn = () => {
    const tile = getTileById(zoomedTile || 0);
    let rollAgain = false;
    setPlayers(prev => {
      const np = prev.map((p, i) => {
        if (i !== currentPlayerIndex) return p;
        const pathIdx = p.pathIndex !== null ? p.pathIndex : 0;
        const pathLength = activePathTiles[pathIdx].length;
        let updated = {
          ...p
        };
        if (tile.effect === 'move' && tile.effectValue) {
          let pos = updated.position + tile.effectValue;
          if (pos >= pathLength) pos = pathLength - 1;
          if (pos < 0) pos = 0;
          updated.position = pos;
        } else if (tile.effect === 'skip') {
          updated.skipNextTurn = true;
        } else if (tile.effect === 'roll_again') {
          rollAgain = true;
        } else if (tile.effect === 'money_loss' && tile.effectValue) {
          updated.money = p.money + tile.effectValue;
        } else if (tile.effect === 'money_gain' && tile.effectValue) {
          updated.money = p.money + tile.effectValue;
        }
        return updated;
      });
      return np;
    });
    setShowPopup(false);
    setZoomedTile(null);
    setDiceValue(null);
    if (!rollAgain) advanceToNextPlayer();
  };
  const closeTaxEvent = () => {
    setPhase('playing');
    setTaxResults([]);
    setZoomedTile(null);
    advanceToNextPlayer();
  };
  const closeEvolution = () => {
    setShowEvolution(false);
  };
  // Handle skip turns
  useEffect(() => {
    if (phase === 'playing' && !showPopup && !isRolling && !jobEffectMessage && !showEvolution) {
      const cp = players[currentPlayerIndex];
      if (cp.skipNextTurn && !eliminatedPlayers.includes(cp.id)) {
        setPlayers(prev => prev.map((p, i) => i === currentPlayerIndex ? {
          ...p,
          skipNextTurn: false
        } : p));
        setTimeout(() => advanceToNextPlayer(), 1500);
      }
    }
  }, [currentPlayerIndex, phase, showPopup, isRolling, jobEffectMessage, showEvolution, eliminatedPlayers]);
  // Auto-scroll Chaos Realm
  useEffect(() => {
    if (phase === 'chaos_realm' && activeChaosTileRef.current) {
      activeChaosTileRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [chaosRealmPosition, phase]);
  const DiceIcon = diceValue ? [Dice1Icon, Dice2Icon, Dice3Icon, Dice4Icon, Dice5Icon, Dice6Icon][diceValue - 1] : DicesIcon;
  // ===================== RENDER =====================
  return <div className="min-h-screen bg-gradient-to-br from-pink-100 via-lavender-100 to-blue-100 overflow-hidden font-sans select-none" data-id="element-1662">
      <FloatingParticles count={40} data-id="element-1663" />

      {/* ========== SETUP ========== */}
      {phase === 'setup' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-1664">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-2xl w-full text-center relative overflow-hidden" data-id="element-1665">
            {/* Decorative glows */}
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/30 rounded-full blur-3xl" data-id="element-1666" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl" data-id="element-1667" />
            <div className="absolute -top-8 -left-8 rotate-12" data-id="element-1668">
              <GlitterHeart size={50} data-id="element-1669" />
            </div>
            <div className="absolute -bottom-8 -right-8 -rotate-12" data-id="element-1670">
              <GlitterHeart size={50} data-id="element-1671" />
            </div>

            <motion.div animate={{
          y: [0, -5, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1672">
              <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-2 pb-1" style={{
            fontFamily: '"Dancing Script", cursive'
          }} data-id="element-1673">
                Trippin' Through Life
              </h1>
            </motion.div>
            <p className="text-purple-400 font-bold mb-2 text-lg" data-id="element-1674">
              The chaotic game of glow-ups, breakdowns & tax season. 💀
            </p>

            {/* 4 Life Paths Preview */}
            <div className="grid grid-cols-2 gap-2 mb-6 mt-4" data-id="element-1675">
              {LIFE_PATHS.map((path, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className={`${path.bgLight} ${path.borderColor} border rounded-xl p-2 text-center`} data-id="element-1676">
                  <span className="text-lg" data-id="element-1677">{path.emoji}</span>
                  <p className={`text-[10px] font-bold ${path.textColor} leading-tight`} data-id="element-1678">
                    {path.name}
                  </p>
                </motion.div>)}
            </div>

            <div className="space-y-3 mb-6 text-left" data-id="element-1679">
              {players.map((player, index) => <motion.div key={player.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} className="flex items-center gap-3 bg-white/60 backdrop-blur-sm p-3 rounded-2xl border border-purple-100/50 shadow-sm" data-id="element-1680">
                  <label className="relative cursor-pointer shrink-0 group" data-id="element-1681">
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleAvatarUpload(player.id, f);
              }} data-id="element-1682" />
                    {player.avatar ? <div className={`w-12 h-12 rounded-full border-[3px] overflow-hidden shadow-lg shadow-purple-200/50 ${PLAYER_BORDER[player.color]}`} data-id="element-1683">
                        <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1684" />
                      </div> : <div className={`w-12 h-12 rounded-full ${PLAYER_COLORS[player.color]} shadow-lg flex items-center justify-center relative`} data-id="element-1685">
                        <CameraIcon className="w-5 h-5 text-white/80" data-id="element-1686" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow" data-id="element-1687">
                          <span className="text-[8px] font-bold text-purple-500" data-id="element-1688">
                            +
                          </span>
                        </div>
                      </div>}
                  </label>
                  <input type="text" value={player.name} onChange={e => updatePlayer(player.id, 'name', e.target.value)} className="flex-1 bg-transparent font-bold text-purple-800 text-lg outline-none min-w-0" placeholder={`Player ${index + 1}`} data-id="element-1689" />
                  <select value={player.color} onChange={e => updatePlayer(player.id, 'color', e.target.value as PlayerColor)} className="bg-white/80 border border-purple-200/50 rounded-xl px-2 py-1.5 text-purple-700 font-bold outline-none text-sm backdrop-blur-sm" data-id="element-1690">
                    {(['pink', 'purple', 'blue', 'teal', 'gold', 'coral'] as PlayerColor[]).map(c => <option key={c} value={c} data-id="element-1691">
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>)}
                  </select>
                  {players.length > 2 && <button onClick={() => removePlayer(player.id)} className="text-rose-400 hover:text-rose-600 font-bold px-2" data-id="element-1692">
                      ✕
                    </button>}
                </motion.div>)}
            </div>
            <p className="text-purple-400 text-sm font-medium mb-6" data-id="element-1693">
              📸 Tap the circle to upload a profile pic!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1694">
              {players.length < 6 && <button onClick={addPlayer} className="px-6 py-3 rounded-full bg-purple-100/80 text-purple-600 font-bold hover:bg-purple-200/80 transition-colors backdrop-blur-sm" data-id="element-1695">
                  + Add Player
                </button>}
              <motion.button onClick={goToPathChoice} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-xl shadow-[0_8px_30px_rgba(236,72,153,0.3)] hover:shadow-[0_12px_40px_rgba(236,72,153,0.4)] transition-shadow" data-id="element-1696">
                NEXT: CHOOSE YOUR PATH 🔀
              </motion.button>
            </div>
          </motion.div>
        </div>}

      {/* ========== PATH CHOICE ========== */}
      {phase === 'path_choice' && <div className="absolute inset-0 overflow-hidden" data-id="element-1697">
          {/* Sky Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-purple-200 via-pink-200 to-blue-200" data-id="element-1698" />

          {/* Animated Clouds */}
          <div className="absolute inset-0 overflow-hidden" data-id="element-1699">
            {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute bg-white/40 rounded-full blur-xl" style={{
          width: `${100 + Math.random() * 150}px`,
          height: `${40 + Math.random() * 60}px`,
          top: `${10 + Math.random() * 30}%`,
          left: `${-20 + Math.random() * 120}%`
        }} animate={{
          x: ['0%', '120vw']
        }} transition={{
          duration: 40 + Math.random() * 30,
          repeat: Infinity,
          ease: 'linear',
          delay: Math.random() * 10
        }} data-id="element-1700" />)}
          </div>

          {/* Floating Sparkles */}
          <FloatingParticles count={80} data-id="element-1701" />

          {/* Ground/Terrain */}
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-b from-green-200/40 via-green-300/30 to-green-400/40" data-id="element-1702" />
          <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-green-500/20 to-transparent" data-id="element-1703" />

          {/* Horizon Glow */}
          <div className="absolute top-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" data-id="element-1704" />

          {/* Top UI - Game Title */}
          <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="absolute top-8 left-0 right-0 text-center z-20" data-id="element-1705">
            <h1 className="text-4xl md:text-5xl font-bold text-white/90 drop-shadow-[0_2px_10px_rgba(0,0,0,0.3)]" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1706">
              Trippin' Through Life
            </h1>
          </motion.div>

          {/* Current Player Indicator */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-24 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2" data-id="element-1707">
            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-md px-6 py-3 rounded-full shadow-lg" data-id="element-1708">
              {players[pathChoicePlayerIndex].avatar ? <img src={players[pathChoicePlayerIndex].avatar!} alt="" className={`w-10 h-10 rounded-full object-cover border-2 ${PLAYER_BORDER[players[pathChoicePlayerIndex].color]}`} data-id="element-1709" /> : <div className={`w-10 h-10 rounded-full ${PLAYER_COLORS[players[pathChoicePlayerIndex].color]} flex items-center justify-center text-white font-bold shadow-lg`} data-id="element-1710">
                  {players[pathChoicePlayerIndex].name.charAt(0)}
                </div>}
              <span className="font-bold text-purple-800 text-lg" data-id="element-1711">
                {players[pathChoicePlayerIndex].name}, choose your path...
              </span>
            </div>
          </motion.div>

          {/* Already Chosen Players - Small Badges */}
          {pathChoicePlayerIndex > 0 && <div className="absolute top-40 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-id="element-1712">
              {players.slice(0, pathChoicePlayerIndex).map(p => <motion.div key={p.id} initial={{
          opacity: 0,
          scale: 0
        }} animate={{
          opacity: 1,
          scale: 1
        }} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-white/70 backdrop-blur-sm border ${p.pathIndex !== null ? LIFE_PATHS[p.pathIndex].borderColor : 'border-gray-200'}`} data-id="element-1713">
                  <span data-id="element-1714">{p.name}</span>
                  {p.pathIndex !== null && <span data-id="element-1715">{LIFE_PATHS[p.pathIndex].emoji}</span>}
                </motion.div>)}
            </div>}

          {/* The Four Paths - Branching from Center Bottom */}
          <div className="absolute inset-0 flex items-end justify-center pb-32" data-id="element-1716">
            <div className="relative w-full max-w-6xl h-full flex items-end justify-center" data-id="element-1717">
              {/* Path Labels - OUTSIDE clipped buttons so they're visible */}
              {/* Path 0: College Label */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="absolute left-[15%] w-32 md:w-40 z-10 pointer-events-none" style={{
            bottom: '62%'
          }} data-id="element-1718">
                <div className="text-center bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg border-2 border-violet-300 mx-auto w-fit" data-id="element-1719">
                  <span className="text-2xl block mb-0.5" data-id="element-1720">🎓</span>
                  <span className="text-xs font-bold text-violet-700 whitespace-nowrap block" data-id="element-1721">
                    College
                  </span>
                  <span className="text-[9px] text-violet-500 font-medium block mt-0.5" data-id="element-1722">
                    High pay • Debt • 3x Chaos
                  </span>
                </div>
              </motion.div>

              {/* Path 1: HS/GED Label */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="absolute left-[38%] w-32 md:w-40 z-10 pointer-events-none" style={{
            bottom: '62%'
          }} data-id="element-1723">
                <div className="text-center bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg border-2 border-pink-300 mx-auto w-fit" data-id="element-1724">
                  <span className="text-2xl block mb-0.5" data-id="element-1725">📚</span>
                  <span className="text-xs font-bold text-pink-700 whitespace-nowrap block" data-id="element-1726">
                    HS/GED
                  </span>
                  <span className="text-[9px] text-pink-500 font-medium block mt-0.5" data-id="element-1727">
                    Steady pay • 2x Chaos
                  </span>
                </div>
              </motion.div>

              {/* Path 2: Dropout Label */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="absolute left-[62%] w-32 md:w-40 z-10 pointer-events-none" style={{
            bottom: '62%'
          }} data-id="element-1728">
                <div className="text-center bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg border-2 border-orange-300 mx-auto w-fit" data-id="element-1729">
                  <span className="text-2xl block mb-0.5" data-id="element-1730">⚡</span>
                  <span className="text-xs font-bold text-orange-700 whitespace-nowrap block" data-id="element-1731">
                    Drop Out
                  </span>
                  <span className="text-[9px] text-orange-500 font-medium block mt-0.5" data-id="element-1732">
                    Low pay • No health costs • 2x Chaos
                  </span>
                </div>
              </motion.div>

              {/* Path 3: Streaming Label */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="absolute left-[85%] w-32 md:w-40 z-10 pointer-events-none" style={{
            bottom: '62%'
          }} data-id="element-1733">
                <div className="text-center bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg border-2 border-teal-300 mx-auto w-fit" data-id="element-1734">
                  <span className="text-2xl block mb-0.5" data-id="element-1735">📺</span>
                  <span className="text-xs font-bold text-teal-700 whitespace-nowrap block" data-id="element-1736">
                    Streaming
                  </span>
                  <span className="text-[9px] text-teal-500 font-medium block mt-0.5" data-id="element-1737">
                    Wild income • Big breaks • 1x Chaos
                  </span>
                </div>
              </motion.div>

              {/* Path 0: College (Left) */}
              <motion.button initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} onClick={() => selectPath(pathChoicePlayerIndex, 0)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="absolute bottom-0 left-[15%] w-32 md:w-40 group cursor-pointer" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1738">
                <div className="absolute inset-0 bg-gradient-to-t from-violet-400 to-violet-200 opacity-70 group-hover:opacity-90 transition-opacity" style={{
              boxShadow: '0 0 30px rgba(139,92,246,0.4)'
            }} data-id="element-1739" />
                <motion.div animate={{
              boxShadow: ['0 0 20px rgba(139,92,246,0.3)', '0 0 40px rgba(139,92,246,0.6)', '0 0 20px rgba(139,92,246,0.3)']
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className="absolute inset-0" style={{
              clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
            }} data-id="element-1740" />

                {/* Path Label */}
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 text-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border-2 border-violet-300" data-id="element-1741">
                  <span className="text-2xl block mb-1" data-id="element-1742">🎓</span>
                  <span className="text-xs font-bold text-violet-700 whitespace-nowrap" data-id="element-1743">
                    College
                  </span>
                </div>

                {/* Silhouette */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1744">
                  <div className="absolute bottom-0 left-2 w-3 h-8 bg-violet-800 rounded-t" data-id="element-1745" />
                  <div className="absolute bottom-0 left-6 w-4 h-10 bg-violet-800 rounded-t" data-id="element-1746" />
                  <div className="absolute bottom-0 left-11 w-3 h-6 bg-violet-800 rounded-t" data-id="element-1747" />
                  <div className="absolute bottom-0 right-2 w-3 h-9 bg-violet-800 rounded-t" data-id="element-1748" />
                </div>
              </motion.button>

              {/* Path 1: HS/GED (Center-Left) */}
              <motion.button initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} onClick={() => selectPath(pathChoicePlayerIndex, 1)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="absolute bottom-0 left-[38%] w-32 md:w-40 group cursor-pointer" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1749">
                <div className="absolute inset-0 bg-gradient-to-t from-pink-400 to-pink-200 opacity-70 group-hover:opacity-90 transition-opacity" style={{
              boxShadow: '0 0 30px rgba(236,72,153,0.4)'
            }} data-id="element-1750" />
                <motion.div animate={{
              boxShadow: ['0 0 20px rgba(236,72,153,0.3)', '0 0 40px rgba(236,72,153,0.6)', '0 0 20px rgba(236,72,153,0.3)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: 0.5
            }} className="absolute inset-0" style={{
              clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
            }} data-id="element-1751" />

                <div className="absolute -top-16 left-1/2 -translate-x-1/2 text-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border-2 border-pink-300" data-id="element-1752">
                  <span className="text-2xl block mb-1" data-id="element-1753">📚</span>
                  <span className="text-xs font-bold text-pink-700 whitespace-nowrap" data-id="element-1754">
                    HS/GED
                  </span>
                </div>

                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1755">
                  <div className="absolute bottom-0 left-1 w-4 h-7 bg-pink-800 rounded-t" data-id="element-1756" />
                  <div className="absolute bottom-0 left-6 w-5 h-9 bg-pink-800 rounded-t" data-id="element-1757" />
                  <div className="absolute bottom-0 right-1 w-4 h-6 bg-pink-800 rounded-t" data-id="element-1758" />
                </div>
              </motion.button>

              {/* Path 2: Dropout (Center-Right) */}
              <motion.button initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} onClick={() => selectPath(pathChoicePlayerIndex, 2)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="absolute bottom-0 left-[62%] w-32 md:w-40 group cursor-pointer" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1759">
                <div className="absolute inset-0 bg-gradient-to-t from-orange-400 to-orange-200 opacity-70 group-hover:opacity-90 transition-opacity" style={{
              boxShadow: '0 0 30px rgba(251,146,60,0.4)',
              backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 12px)'
            }} data-id="element-1760" />
                <motion.div animate={{
              boxShadow: ['0 0 20px rgba(251,146,60,0.3)', '0 0 40px rgba(251,146,60,0.6)', '0 0 20px rgba(251,146,60,0.3)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: 1
            }} className="absolute inset-0" style={{
              clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
            }} data-id="element-1761" />

                <div className="absolute -top-16 left-1/2 -translate-x-1/2 text-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border-2 border-orange-300" data-id="element-1762">
                  <span className="text-2xl block mb-1" data-id="element-1763">⚡</span>
                  <span className="text-xs font-bold text-orange-700 whitespace-nowrap" data-id="element-1764">
                    Drop Out
                  </span>
                </div>

                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-20" data-id="element-1765">
                  <div className="absolute bottom-0 left-0 w-3 h-5 bg-orange-900 rounded-t transform -rotate-6" data-id="element-1766" />
                  <div className="absolute bottom-0 left-5 w-4 h-7 bg-orange-900 rounded-t" data-id="element-1767" />
                  <div className="absolute bottom-0 right-3 w-3 h-4 bg-orange-900 rounded-t transform rotate-12" data-id="element-1768" />
                </div>
              </motion.button>

              {/* Path 3: Self-Employed (Right) */}
              <motion.button initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} onClick={() => selectPath(pathChoicePlayerIndex, 3)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="absolute bottom-0 left-[85%] w-32 md:w-40 group cursor-pointer" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1769">
                <div className="absolute inset-0 bg-gradient-to-t from-teal-400 to-cyan-200 opacity-70 group-hover:opacity-90 transition-opacity" style={{
              boxShadow: '0 0 30px rgba(20,184,166,0.4)'
            }} data-id="element-1770" />
                <motion.div animate={{
              boxShadow: ['0 0 20px rgba(20,184,166,0.3)', '0 0 40px rgba(20,184,166,0.6)', '0 0 20px rgba(20,184,166,0.3)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: 1.5
            }} className="absolute inset-0" style={{
              clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
            }} data-id="element-1771" />

                {/* Neon accent lines */}
                <div className="absolute inset-0 opacity-40" data-id="element-1772">
                  <div className="absolute bottom-1/4 left-0 right-0 h-px bg-cyan-300" data-id="element-1773" />
                  <div className="absolute bottom-1/2 left-0 right-0 h-px bg-cyan-300" data-id="element-1774" />
                  <div className="absolute bottom-3/4 left-0 right-0 h-px bg-cyan-300" data-id="element-1775" />
                </div>

                <div className="absolute -top-16 left-1/2 -translate-x-1/2 text-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border-2 border-teal-300" data-id="element-1776">
                  <span className="text-2xl block mb-1" data-id="element-1777">📺</span>
                  <span className="text-xs font-bold text-teal-700 whitespace-nowrap" data-id="element-1778">
                    Streaming
                  </span>
                </div>

                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1779">
                  <div className="absolute bottom-0 left-2 w-2 h-6 bg-teal-800 rounded-t" data-id="element-1780" />
                  <div className="absolute bottom-0 left-6 w-3 h-8 bg-teal-800 rounded-t" data-id="element-1781" />
                  <div className="absolute bottom-0 left-10 w-2 h-5 bg-teal-800 rounded-t" data-id="element-1782" />
                  <div className="absolute bottom-0 right-3 w-3 h-7 bg-teal-800 rounded-t" data-id="element-1783" />
                </div>
              </motion.button>
            </div>
          </div>

          {/* Player Avatars at Starting Line */}
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-4 z-30" data-id="element-1784">
            {players.map((player, i) => <motion.div key={player.id} initial={{
          opacity: 0,
          scale: 0,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          delay: 0.6 + i * 0.1
        }} className="relative" data-id="element-1785">
                {player.avatar ? <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full border-4 ${PLAYER_BORDER[player.color]} shadow-[0_0_20px_rgba(255,255,255,0.5)] overflow-hidden`} data-id="element-1786">
                    <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1787" />
                  </div> : <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full border-4 ${PLAYER_BORDER[player.color]} ${PLAYER_COLORS[player.color]} shadow-[0_0_20px_rgba(255,255,255,0.5)] flex items-center justify-center text-white font-bold text-xl`} data-id="element-1788">
                    {player.name.charAt(0)}
                  </div>}

                {/* Show path emoji if already chosen */}
                {player.pathIndex !== null && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg text-sm" data-id="element-1789">
                    {LIFE_PATHS[player.pathIndex].emoji}
                  </motion.div>}
              </motion.div>)}
          </div>

          {/* Bottom Instruction */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center z-20" data-id="element-1790">
            <p className="text-white/80 text-sm font-medium drop-shadow-lg" data-id="element-1791">
              ✨ Tap a path to choose your destiny ✨
            </p>
          </motion.div>
        </div>}

      {/* ========== JOURNEY START ========== */}
      {phase === 'journey_start' && <div className="absolute inset-0 overflow-hidden" data-id="element-1792">
          {/* Sky Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-purple-200 via-pink-200 to-blue-200" data-id="element-1793" />

          {/* Animated Clouds */}
          <div className="absolute inset-0 overflow-hidden" data-id="element-1794">
            {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute bg-white/40 rounded-full blur-xl" style={{
          width: `${100 + Math.random() * 150}px`,
          height: `${40 + Math.random() * 60}px`,
          top: `${10 + Math.random() * 30}%`,
          left: `${-20 + Math.random() * 120}%`
        }} animate={{
          x: ['0%', '120vw']
        }} transition={{
          duration: 40 + Math.random() * 30,
          repeat: Infinity,
          ease: 'linear',
          delay: Math.random() * 10
        }} data-id="element-1795" />)}
          </div>

          {/* Floating Sparkles */}
          <FloatingParticles count={80} data-id="element-1796" />

          {/* Ground/Terrain */}
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-b from-green-200/40 via-green-300/30 to-green-400/40" data-id="element-1797" />
          <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-green-500/20 to-transparent" data-id="element-1798" />

          {/* Horizon Glow */}
          <div className="absolute top-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" data-id="element-1799" />

          {/* Top UI - Title & Subtitle */}
          <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="absolute top-8 left-0 right-0 text-center z-20 flex flex-col items-center" data-id="element-1800">
            <div className="flex items-center gap-3" data-id="element-1801">
              <SparklesIcon className="w-8 h-8 text-purple-600" data-id="element-1802" />
              <h1 className="text-5xl md:text-6xl font-bold text-purple-800 drop-shadow-[0_2px_10px_rgba(255,255,255,0.8)]" style={{
            fontFamily: '"Dancing Script", cursive'
          }} data-id="element-1803">
                Your Journey Begins!
              </h1>
              <SparklesIcon className="w-8 h-8 text-purple-600" data-id="element-1804" />
            </div>
            <p className="mt-2 text-sm md:text-base font-bold text-purple-900 tracking-widest uppercase bg-white/50 px-6 py-1.5 rounded-full backdrop-blur-sm" data-id="element-1805">
              Follow your path. Make your choices. Build your life.
            </p>
          </motion.div>

          {/* The Four Paths */}
          <div className="absolute inset-0 flex items-end justify-center pb-32" data-id="element-1806">
            <div className="relative w-full max-w-6xl h-full flex items-end justify-center" data-id="element-1807">
              {/* Path Info Cards */}
              {/* College */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="absolute left-[15%] w-48 md:w-56 z-10 pointer-events-none -translate-x-1/2" style={{
            bottom: '55%'
          }} data-id="element-1808">
                <div className="text-center bg-violet-800/90 backdrop-blur-md rounded-xl shadow-xl border-4 border-violet-400 overflow-hidden" data-id="element-1809">
                  <div className="bg-violet-900 py-2" data-id="element-1810">
                    <span className="text-3xl block mb-1" data-id="element-1811">🎓</span>
                    <span className="text-sm font-bold text-white uppercase tracking-wider block" data-id="element-1812">
                      College
                      <br data-id="element-1813" />
                      Best Job
                    </span>
                  </div>
                  <div className="bg-violet-200/90 py-2 px-2" data-id="element-1814">
                    <span className="text-[10px] text-violet-900 font-bold block uppercase" data-id="element-1815">
                      High Pay • High Debt
                      <br data-id="element-1816" />
                      More Chaos
                    </span>
                  </div>
                  <div className="bg-violet-800 py-1.5" data-id="element-1817">
                    <span className="text-[10px] text-white font-bold uppercase" data-id="element-1818">
                      3 Chaos Chances
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* HS/GED */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="absolute left-[38%] w-48 md:w-56 z-10 pointer-events-none -translate-x-1/2" style={{
            bottom: '55%'
          }} data-id="element-1819">
                <div className="text-center bg-pink-600/90 backdrop-blur-md rounded-xl shadow-xl border-4 border-pink-300 overflow-hidden" data-id="element-1820">
                  <div className="bg-pink-700 py-2" data-id="element-1821">
                    <span className="text-3xl block mb-1" data-id="element-1822">📚</span>
                    <span className="text-sm font-bold text-white uppercase tracking-wider block" data-id="element-1823">
                      HS / GED
                      <br data-id="element-1824" />
                      Good Job
                    </span>
                  </div>
                  <div className="bg-pink-200/90 py-2 px-2" data-id="element-1825">
                    <span className="text-[10px] text-pink-900 font-bold block uppercase" data-id="element-1826">
                      Steady Income
                      <br data-id="element-1827" />
                      Moderate Chaos
                    </span>
                  </div>
                  <div className="bg-pink-700 py-1.5" data-id="element-1828">
                    <span className="text-[10px] text-white font-bold uppercase" data-id="element-1829">
                      2 Chaos Chances
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Dropout */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="absolute left-[62%] w-48 md:w-56 z-10 pointer-events-none -translate-x-1/2" style={{
            bottom: '55%'
          }} data-id="element-1830">
                <div className="text-center bg-orange-600/90 backdrop-blur-md rounded-xl shadow-xl border-4 border-orange-300 overflow-hidden" data-id="element-1831">
                  <div className="bg-orange-700 py-2" data-id="element-1832">
                    <span className="text-3xl block mb-1" data-id="element-1833">⚡</span>
                    <span className="text-sm font-bold text-white uppercase tracking-wider block" data-id="element-1834">
                      Dropout
                      <br data-id="element-1835" />
                      Min Wage
                    </span>
                  </div>
                  <div className="bg-orange-200/90 py-2 px-2" data-id="element-1836">
                    <span className="text-[10px] text-orange-900 font-bold block uppercase" data-id="element-1837">
                      Low Income • Survival Mode
                      <br data-id="element-1838" />
                      No Food or Health Expenses
                    </span>
                  </div>
                  <div className="bg-orange-700 py-1.5 flex flex-col gap-1" data-id="element-1839">
                    <span className="text-[10px] text-white font-bold uppercase" data-id="element-1840">
                      2 Chaos Chances
                    </span>
                  </div>
                </div>
                <div className="mt-2 bg-green-800/80 backdrop-blur-sm rounded-lg p-2 border border-green-600 shadow-lg mx-auto w-fit" data-id="element-1841">
                  <div className="text-[9px] text-green-100 font-bold flex items-center gap-1" data-id="element-1842">
                    <CheckIcon className="w-3 h-3 text-green-400" data-id="element-1843" /> Food:
                    Covered
                  </div>
                  <div className="text-[9px] text-green-100 font-bold flex items-center gap-1" data-id="element-1844">
                    <CheckIcon className="w-3 h-3 text-green-400" data-id="element-1845" /> Healthcare:
                    Covered
                  </div>
                </div>
              </motion.div>

              {/* Streaming */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="absolute left-[85%] w-48 md:w-56 z-10 pointer-events-none -translate-x-1/2" style={{
            bottom: '55%'
          }} data-id="element-1846">
                <div className="text-center bg-teal-600/90 backdrop-blur-md rounded-xl shadow-xl border-4 border-teal-300 overflow-hidden" data-id="element-1847">
                  <div className="bg-teal-700 py-2" data-id="element-1848">
                    <span className="text-3xl block mb-1" data-id="element-1849">📺</span>
                    <span className="text-sm font-bold text-white uppercase tracking-wider block" data-id="element-1850">
                      Self-Employed
                      <br data-id="element-1851" />
                      Streaming
                    </span>
                  </div>
                  <div className="bg-teal-200/90 py-2 px-2" data-id="element-1852">
                    <span className="text-[10px] text-teal-900 font-bold block uppercase" data-id="element-1853">
                      High Risk • High Reward
                      <br data-id="element-1854" />
                      Big Break Potential
                    </span>
                  </div>
                  <div className="bg-teal-700 py-1.5" data-id="element-1855">
                    <span className="text-[10px] text-white font-bold uppercase" data-id="element-1856">
                      1 Chaos Chance
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Path 0: College */}
              <div className="absolute bottom-0 left-[15%] w-32 md:w-40" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1857">
                <div className="absolute inset-0 bg-gradient-to-t from-violet-400 to-violet-200 opacity-70" style={{
              boxShadow: '0 0 30px rgba(139,92,246,0.4)'
            }} data-id="element-1858" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1859">
                  <div className="absolute bottom-0 left-2 w-3 h-8 bg-violet-800 rounded-t" data-id="element-1860" />
                  <div className="absolute bottom-0 left-6 w-4 h-10 bg-violet-800 rounded-t" data-id="element-1861" />
                  <div className="absolute bottom-0 left-11 w-3 h-6 bg-violet-800 rounded-t" data-id="element-1862" />
                  <div className="absolute bottom-0 right-2 w-3 h-9 bg-violet-800 rounded-t" data-id="element-1863" />
                </div>
              </div>

              {/* Path 1: HS/GED */}
              <div className="absolute bottom-0 left-[38%] w-32 md:w-40" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1864">
                <div className="absolute inset-0 bg-gradient-to-t from-pink-400 to-pink-200 opacity-70" style={{
              boxShadow: '0 0 30px rgba(236,72,153,0.4)'
            }} data-id="element-1865" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1866">
                  <div className="absolute bottom-0 left-1 w-4 h-7 bg-pink-800 rounded-t" data-id="element-1867" />
                  <div className="absolute bottom-0 left-6 w-5 h-9 bg-pink-800 rounded-t" data-id="element-1868" />
                  <div className="absolute bottom-0 right-1 w-4 h-6 bg-pink-800 rounded-t" data-id="element-1869" />
                </div>
              </div>

              {/* Path 2: Dropout */}
              <div className="absolute bottom-0 left-[62%] w-32 md:w-40" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1870">
                <div className="absolute inset-0 bg-gradient-to-t from-orange-400 to-orange-200 opacity-70" style={{
              boxShadow: '0 0 30px rgba(251,146,60,0.4)',
              backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 12px)'
            }} data-id="element-1871" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-20" data-id="element-1872">
                  <div className="absolute bottom-0 left-0 w-3 h-5 bg-orange-900 rounded-t transform -rotate-6" data-id="element-1873" />
                  <div className="absolute bottom-0 left-5 w-4 h-7 bg-orange-900 rounded-t" data-id="element-1874" />
                  <div className="absolute bottom-0 right-3 w-3 h-4 bg-orange-900 rounded-t transform rotate-12" data-id="element-1875" />
                </div>
              </div>

              {/* Path 3: Streaming */}
              <div className="absolute bottom-0 left-[85%] w-32 md:w-40" style={{
            height: '60%',
            clipPath: 'polygon(40% 100%, 60% 100%, 55% 0%, 45% 0%)'
          }} data-id="element-1876">
                <div className="absolute inset-0 bg-gradient-to-t from-teal-400 to-cyan-200 opacity-70" style={{
              boxShadow: '0 0 30px rgba(20,184,166,0.4)'
            }} data-id="element-1877" />
                <div className="absolute inset-0 opacity-40" data-id="element-1878">
                  <div className="absolute bottom-1/4 left-0 right-0 h-px bg-cyan-300" data-id="element-1879" />
                  <div className="absolute bottom-1/2 left-0 right-0 h-px bg-cyan-300" data-id="element-1880" />
                  <div className="absolute bottom-3/4 left-0 right-0 h-px bg-cyan-300" data-id="element-1881" />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-12 opacity-30" data-id="element-1882">
                  <div className="absolute bottom-0 left-2 w-2 h-6 bg-teal-800 rounded-t" data-id="element-1883" />
                  <div className="absolute bottom-0 left-6 w-3 h-8 bg-teal-800 rounded-t" data-id="element-1884" />
                  <div className="absolute bottom-0 left-10 w-2 h-5 bg-teal-800 rounded-t" data-id="element-1885" />
                  <div className="absolute bottom-0 right-3 w-3 h-7 bg-teal-800 rounded-t" data-id="element-1886" />
                </div>
              </div>
            </div>
          </div>

          {/* START Text on Ground */}
          <div className="absolute bottom-6 left-0 right-0 text-center z-10 pointer-events-none opacity-40" data-id="element-1887">
            <h2 className="text-7xl md:text-9xl font-black text-white tracking-[0.2em] uppercase" style={{
          transform: 'perspective(500px) rotateX(60deg)'
        }} data-id="element-1888">
              START
            </h2>
          </div>

          {/* Player Avatars at Starting Line */}
          <div className="absolute bottom-24 left-0 right-0 z-30 pointer-events-none" data-id="element-1889">
            <div className="relative w-full max-w-6xl mx-auto h-full" data-id="element-1890">
              {players.map((player, i) => {
            const pathPositions = {
              0: '15%',
              1: '38%',
              2: '62%',
              3: '85%'
            };
            const leftPos = player.pathIndex !== null ? pathPositions[player.pathIndex as keyof typeof pathPositions] : '50%';
            // If multiple players on same path, offset them slightly
            const playersOnSamePath = players.filter(p => p.pathIndex === player.pathIndex);
            const indexOnPath = playersOnSamePath.findIndex(p => p.id === player.id);
            const offset = playersOnSamePath.length > 1 ? (indexOnPath - (playersOnSamePath.length - 1) / 2) * 40 : 0;
            return <motion.div key={player.id} initial={{
              opacity: 0,
              scale: 0,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} transition={{
              delay: 0.8 + i * 0.1
            }} className="absolute bottom-0 -translate-x-1/2 flex flex-col items-center" style={{
              left: `calc(${leftPos} + ${offset}px)`
            }} data-id="element-1891">
                    {/* Glowing Circle beneath player */}
                    <div className="absolute -bottom-4 w-24 h-8 rounded-full border-4 opacity-80" style={{
                borderColor: LIFE_PATHS[player.pathIndex || 0].color.split(' ')[1].replace('to-', ''),
                boxShadow: `0 0 20px ${LIFE_PATHS[player.pathIndex || 0].glowColor}`,
                transform: 'perspective(200px) rotateX(60deg)'
              }} data-id="element-1892" />

                    {player.avatar ? <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-4 ${PLAYER_BORDER[player.color]} shadow-[0_0_20px_rgba(255,255,255,0.5)] overflow-hidden relative z-10`} data-id="element-1893">
                        <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1894" />
                      </div> : <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-4 ${PLAYER_BORDER[player.color]} ${PLAYER_COLORS[player.color]} shadow-[0_0_20px_rgba(255,255,255,0.5)] flex items-center justify-center text-white font-bold text-2xl relative z-10`} data-id="element-1895">
                        {player.name.charAt(0)}
                      </div>}

                    {/* Player Name & Job Badge */}
                    <div className="mt-3 bg-indigo-900/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-indigo-400 shadow-lg flex flex-col items-center relative z-10" data-id="element-1896">
                      <span className="text-white font-bold text-sm uppercase tracking-wider" data-id="element-1897">
                        {player.pathIndex !== null && LIFE_PATHS[player.pathIndex].emoji}{' '}
                        {player.name}
                      </span>
                      {player.job && <div className="flex items-center gap-1 mt-0.5" data-id="element-1898">
                          <span className="text-xs" data-id="element-1899">{player.job.emoji}</span>
                          <span className="text-[10px] text-indigo-200 font-medium truncate max-w-[80px]" data-id="element-1900">
                            {player.job.name}
                          </span>
                        </div>}
                    </div>
                  </motion.div>;
          })}
            </div>
          </div>

          {/* Roll Dice CTA */}
          <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 1.5
      }} className="absolute bottom-8 left-8 z-40" data-id="element-1901">
            <button onClick={startActualPlaying} className="group flex items-center gap-4 bg-indigo-900/90 backdrop-blur-md p-2 pr-6 rounded-full border-2 border-indigo-400 shadow-[0_0_30px_rgba(79,70,229,0.5)] hover:bg-indigo-800 transition-all hover:scale-105" data-id="element-1902">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-inner relative overflow-hidden" data-id="element-1903">
                <DicesIcon className="w-8 h-8 text-indigo-600 group-hover:animate-bounce" data-id="element-1904" />
                <SparklesIcon className="absolute top-1 right-1 w-4 h-4 text-amber-400" data-id="element-1905" />
              </div>
              <div className="flex flex-col items-start" data-id="element-1906">
                <span className="text-white font-bold text-sm" data-id="element-1907">
                  Roll the dice to move forward
                </span>
                <span className="text-indigo-200 text-xs" data-id="element-1908">
                  and see what life brings!
                </span>
              </div>
            </button>
          </motion.div>
        </div>}

      {/* ========== PLAYING ========== */}
      {phase === 'playing' && <div className="absolute inset-0 flex flex-col" data-id="element-1909">
          {/* Header */}
          <div className="p-3 flex justify-between items-center bg-white/50 backdrop-blur-xl border-b border-white/60 z-20 shadow-[0_4px_20px_rgba(168,85,247,0.08)]" data-id="element-1910">
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1911">
              Trippin' Through Life
            </h1>
            <div className="flex items-center gap-2" data-id="element-1912">
              {players[currentPlayerIndex].avatar && <img src={players[currentPlayerIndex].avatar!} alt="" className="w-7 h-7 rounded-full object-cover border-2 border-white shadow" data-id="element-1913" />}
              <div className={`px-3 py-1 rounded-full text-white font-bold text-xs shadow-lg ${PLAYER_COLORS[players[currentPlayerIndex].color]}`} data-id="element-1914">
                {players[currentPlayerIndex].name}
              </div>
              {players[currentPlayerIndex].job && <span className="text-sm" data-id="element-1915">
                  {players[currentPlayerIndex].job!.emoji}
                </span>}
              {players[currentPlayerIndex].pathIndex === 0 && players[currentPlayerIndex].studentLoanDebt > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-id="element-1916">
                    💳 Loans
                  </span>}
              <span className={`font-bold text-sm ${players[currentPlayerIndex].money < 0 ? 'text-red-500' : 'text-emerald-600'}`} data-id="element-1917">
                ${players[currentPlayerIndex].money}
              </span>
            </div>
          </div>

          {/* Job Effect Message */}
          <AnimatePresence data-id="element-1918">
            {jobEffectMessage && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="absolute top-16 left-1/2 -translate-x-1/2 z-40 bg-white/90 backdrop-blur-2xl px-6 py-3 rounded-2xl shadow-[0_8px_30px_rgba(168,85,247,0.15)] border border-purple-200/50 text-center max-w-md" data-id="element-1919">
                <p className="font-bold text-gray-800 text-lg" data-id="element-1920">
                  {jobEffectMessage}
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Income Flash */}
          <AnimatePresence data-id="element-1921">
            {incomeFlash && <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.8
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -30
        }} className="absolute top-16 right-4 z-40 bg-emerald-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-[0_4px_20px_rgba(16,185,129,0.4)]" data-id="element-1922">
                {incomeFlash}
              </motion.div>}
          </AnimatePresence>

          {/* Evolution Popup */}
          <AnimatePresence data-id="element-1923">
            {showEvolution && evolvedJob && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-id="element-1924">
                <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 100
          }} className="bg-white/90 backdrop-blur-2xl p-8 rounded-[2rem] shadow-[0_20px_80px_rgba(251,191,36,0.3)] border-4 border-amber-300 max-w-md w-full text-center" data-id="element-1925">
                  <SparklesIcon className="w-16 h-16 mx-auto text-amber-400 mb-4" data-id="element-1926" />
                  <h2 className="text-3xl font-bold text-gray-800 mb-2" data-id="element-1927">
                    🧠 → {evolvedJob.emoji}
                  </h2>
                  <h3 className="text-2xl font-bold text-purple-700 mb-2" data-id="element-1928">
                    Your Hustle Paid Off!
                  </h3>
                  <p className="text-lg font-bold text-gray-700 mb-1" data-id="element-1929">
                    {players[currentPlayerIndex].name} evolved into:
                  </p>
                  <p className="text-3xl font-bold text-emerald-600 mb-2" data-id="element-1930">
                    {evolvedJob.emoji} {evolvedJob.name}
                  </p>
                  <p className="text-gray-500 mb-6" data-id="element-1931">
                    ${evolvedJob.wage}/hr • {evolvedJob.taxType}
                  </p>
                  <motion.button onClick={closeEvolution} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-lg shadow-lg" data-id="element-1932">
                    LET'S GET THIS MONEY 💰
                  </motion.button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* ===== THE BOARD ===== */}
          <div className="flex-1 relative overflow-hidden bg-gradient-to-b from-sky-200 via-purple-100 to-pink-100" ref={boardRef} data-id="element-1933">
            <FloatingParticles count={40} data-id="element-1934" />
            <SparkleBurst active={showBurst} data-id="element-1935" />

            {/* Camera Container */}
            <motion.div className="absolute top-0 left-0 w-full h-[5000px]" initial={{
          y: -5000,
          opacity: 0
        }} animate={boardLanded ? {
          y: `calc(60vh - ${getBoardPosition(activePathTiles[players[currentPlayerIndex].pathIndex !== null ? players[currentPlayerIndex].pathIndex : 0][Math.min(players[currentPlayerIndex].position, activePathTiles[players[currentPlayerIndex].pathIndex !== null ? players[currentPlayerIndex].pathIndex : 0].length - 1)] || 0, activePathTiles).y / 100 * 5000}px)`,
          opacity: 1
        } : {
          y: -5000,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: boardLanded ? 40 : 60,
          damping: boardLanded ? 15 : 18,
          mass: 1.2
        }} data-id="element-1936">
              {/* Path Lanes */}
              {LIFE_PATHS.map((path, i) => {
            const xPositions = [15, 38, 62, 85];
            return <div key={`lane-${i}`} className="absolute top-0 bottom-0 w-[12%] -translate-x-1/2 rounded-full opacity-40" style={{
              left: `${xPositions[i]}%`,
              background: `linear-gradient(to bottom, transparent, ${path.glowColor}, transparent)`,
              boxShadow: glowingPath === i ? `0 0 40px ${LIFE_PATHS[Math.max(0, tilePath)]?.glowColor || 'rgba(255,255,255,0.6)'}` : 'none',
              transition: 'box-shadow 0.8s ease-in-out'
            }} data-id="element-1937" />;
          })}

              {/* Path Labels (placed near the start) */}
              {LIFE_PATHS.map((path, i) => {
            const xPositions = [15, 38, 62, 85];
            return <motion.div key={`label-${i}`} className={`absolute ${path.bgLight} ${path.borderColor} border-2 rounded-xl px-4 py-2 backdrop-blur-sm -translate-x-1/2 -translate-y-1/2 z-10`} style={{
              left: `${xPositions[i]}%`,
              top: '97%'
            }} animate={{
              boxShadow: glowingPath === i ? `0 0 25px ${path.glowColor}` : '0 0 0px transparent',
              scale: glowingPath === i ? 1.05 : 1
            }} transition={{
              duration: 0.8,
              ease: 'easeInOut'
            }} data-id="element-1938">
                    <div className="flex flex-col items-center gap-1" data-id="element-1939">
                      <span className="text-2xl" data-id="element-1940">{path.emoji}</span>
                      <span className={`text-xs font-bold ${path.textColor} text-center leading-tight`} data-id="element-1941">
                        {path.name}
                      </span>
                    </div>
                  </motion.div>;
          })}

              {/* Tiles */}
              {TILES.filter(tile => {
            if (tile.id === 0 || tile.id === 29 || tile.id === 10 || tile.id === 20 || tile.id === 28) return true;
            return activePathTiles.some(path => path.includes(tile.id));
          }).map(tile => {
            const pos = getBoardPosition(tile.id, activePathTiles);
            const style = CATEGORY_STYLES[tile.category];
            const Icon = style.icon;
            const isStart = tile.id === 0;
            const isFinish = tile.id === 29;
            const isMoneyLoss = tile.category === 'money_loss';
            const isMoneyGain = tile.category === 'money' && tile.effect === 'money_gain';
            // Determine which path this tile belongs to for glow
            let tilePath = -1;
            activePathTiles.forEach((pathTiles, idx) => {
              if (pathTiles.includes(tile.id)) tilePath = idx;
            });
            const isFullWidth = isStart || isFinish || tile.id === 10 || tile.id === 20 || tile.id === 28;
            const widthClass = isFullWidth ? 'w-[60%]' : 'w-[18%]';
            const heightClass = isFullWidth ? 'h-[120px]' : 'h-[90px]';
            return <motion.div key={tile.id} className={`absolute ${widthClass} ${heightClass} -translate-x-1/2 -translate-y-1/2 rounded-2xl border-4 ${style.border} ${style.bg} flex flex-col items-center justify-center p-2 text-center z-[2] shadow-lg`} style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`
            }} animate={{
              boxShadow: tilePath === glowingPath || isFullWidth ? `0 0 20px ${LIFE_PATHS[Math.max(0, tilePath)]?.glowColor || 'rgba(255,255,255,0.6)'}` : isMoneyLoss ? '0 0 15px rgba(239,68,68,0.3)' : isMoneyGain ? '0 0 15px rgba(34,197,94,0.3)' : '0 4px 12px rgba(0,0,0,0.1)'
            }} transition={{
              duration: 0.8
            }} whileHover={{
              scale: 1.1,
              zIndex: 10
            }} data-id="element-1942">
                    <span className="absolute top-1 left-2 text-[10px] font-bold text-black/20" data-id="element-1943">
                      {tile.id + 1}
                    </span>
                    <Icon className={`w-6 h-6 md:w-8 md:h-8 mb-1 ${isMoneyLoss ? 'text-red-500 opacity-90' : isMoneyGain ? 'text-emerald-500 opacity-90' : 'opacity-70'}`} data-id="element-1944" />
                    <p className={`text-[9px] md:text-[11px] font-bold leading-tight line-clamp-2 px-1 ${isMoneyLoss ? 'text-red-700' : isMoneyGain ? 'text-emerald-700' : 'text-gray-800'}`} data-id="element-1945">
                      {tile.name}
                    </p>
                    {isMoneyLoss && tile.effectValue && <span className="text-[10px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5 mt-1" data-id="element-1946">
                        -${Math.abs(tile.effectValue)}
                      </span>}
                    {isMoneyGain && tile.effectValue && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 rounded-full px-2 py-0.5 mt-1" data-id="element-1947">
                        +${tile.effectValue}
                      </span>}
                  </motion.div>;
          })}

              {/* Player Pieces */}
              {players.map((player, pIdx) => {
            const pathIdx = player.pathIndex !== null ? player.pathIndex : 0;
            const pathTiles = activePathTiles[pathIdx];
            const tileId = pathTiles[Math.min(player.position, pathTiles.length - 1)] || 0;
            const pos = getBoardPosition(tileId, activePathTiles);
            // Calculate offset for multiple players on same tile
            const playersOnSameTile = players.filter(p => {
              const pPathIdx = p.pathIndex !== null ? p.pathIndex : 0;
              const pTileId = activePathTiles[pPathIdx][Math.min(p.position, activePathTiles[pPathIdx].length - 1)] || 0;
              return pTileId === tileId;
            });
            const indexOnTile = playersOnSameTile.findIndex(p => p.id === player.id);
            const totalOnTile = playersOnSameTile.length;
            // Spread horizontally if multiple players
            const spreadWidth = 8; // percentage
            const ox = totalOnTile > 1 ? (indexOnTile - (totalOnTile - 1) / 2) * spreadWidth : 0;
            const oy = 0;
            return <motion.div key={player.id} className={`absolute w-12 h-12 md:w-14 md:h-14 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 ${PLAYER_BORDER[player.color]} flex items-center justify-center text-white font-bold text-sm z-30 overflow-hidden shadow-xl ${!player.avatar ? PLAYER_COLORS[player.color] : 'bg-white'}`} initial={false} animate={{
              left: `calc(${pos.x}% + ${ox}%)`,
              top: `calc(${pos.y}% + ${oy}%)`,
              rotate: boardLanded ? [0, -5, 5, -3, 0] : 0
            }} transition={{
              type: 'spring',
              stiffness: 50,
              damping: 15
            }} data-id="element-1948">
                    {player.avatar ? <img src={player.avatar} alt="" className="w-full h-full object-cover" data-id="element-1949" /> : player.name.charAt(0).toUpperCase()}
                  </motion.div>;
          })}
            </motion.div>

            {/* Tile Popup */}
            <AnimatePresence data-id="element-1950">
              {showPopup && zoomedTile !== null && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center z-50 p-4 bg-black/20 backdrop-blur-sm" data-id="element-1951">
                  <motion.div initial={{
              scale: 0.7,
              y: 30
            }} animate={{
              scale: 1,
              y: 0
            }} exit={{
              scale: 0.7,
              y: 30
            }} transition={{
              type: 'spring',
              stiffness: 100,
              damping: 18
            }} className={`bg-white/90 backdrop-blur-2xl p-8 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.1)] border-4 ${CATEGORY_STYLES[getTileById(zoomedTile).category].border} max-w-md w-full text-center`} data-id="element-1952">
                    {createElement(CATEGORY_STYLES[getTileById(zoomedTile).category].icon, {
                className: `w-16 h-16 mx-auto mb-3 ${getTileById(zoomedTile).category === 'money_loss' ? 'text-red-500 opacity-90' : getTileById(zoomedTile).effect === 'money_gain' ? 'text-emerald-500 opacity-90' : 'opacity-70'}`
              })}
                    <div className={`inline-block px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wider mb-3 ${getTileById(zoomedTile).category === 'money_loss' ? 'bg-red-100 text-red-600' : getTileById(zoomedTile).effect === 'money_gain' ? 'bg-emerald-100 text-emerald-600' : 'bg-black/5 text-black/50'}`} data-id="element-1953">
                      {getTileById(zoomedTile).category === 'money_loss' ? '💸 money hit' : getTileById(zoomedTile).effect === 'money_gain' ? '💰 money win' : getTileById(zoomedTile).category}
                    </div>
                    <h2 className={`font-heading text-2xl md:text-3xl font-bold mb-3 ${getTileById(zoomedTile).category === 'money_loss' ? 'text-red-700' : getTileById(zoomedTile).effect === 'money_gain' ? 'text-emerald-700' : 'text-gray-800'}`} data-id="element-1954">
                      {getTileById(zoomedTile).name}
                    </h2>
                    <p className="text-lg font-bold text-gray-500 mb-2" data-id="element-1955">
                      {getTileById(zoomedTile).actionText}
                    </p>
                    {getTileById(zoomedTile).effect === 'money_loss' && getTileById(zoomedTile).effectValue && <motion.div initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1,
                x: [0, -4, 4, -4, 4, 0]
              }} transition={{
                duration: 0.5
              }} className="text-3xl font-bold text-red-600 mb-4 py-2 px-4 bg-red-50 rounded-2xl border-2 border-red-200 inline-block" data-id="element-1956">
                          -${Math.abs(getTileById(zoomedTile).effectValue!)}
                        </motion.div>}
                    {getTileById(zoomedTile).effect === 'money_gain' && getTileById(zoomedTile).effectValue && <motion.div initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: [1, 1.15, 1],
                opacity: 1
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="text-3xl font-bold text-emerald-600 mb-4 py-2 px-4 bg-emerald-50 rounded-2xl border-2 border-emerald-200 inline-block" data-id="element-1957">
                          +${getTileById(zoomedTile).effectValue}
                        </motion.div>}
                    {getTileById(zoomedTile).effect !== 'money_loss' && getTileById(zoomedTile).effect !== 'money_gain' && <div className="mb-4" data-id="element-1958" />}
                    <motion.button onClick={applyEffectAndNextTurn} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className="w-full py-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 text-white font-bold text-lg shadow-lg" data-id="element-1959">
                      {zoomedTile === 29 ? 'Claim Victory!' : 'Next Player'}
                    </motion.button>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Transportation Event Popup */}
          <AnimatePresence data-id="element-1960">
            {transportEvent && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 flex items-center justify-center z-50 p-4 bg-black/30 backdrop-blur-sm" data-id="element-1961">
                <motion.div initial={{
            scale: 0.6,
            y: 40
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.6,
            y: 40,
            opacity: 0
          }} transition={{
            type: 'spring',
            stiffness: 120,
            damping: 16
          }} className="bg-white/95 backdrop-blur-2xl p-7 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)] border-2 border-orange-200 max-w-sm w-full text-center" data-id="element-1962">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1,
              rotate: [0, -10, 10, -5, 0]
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4 border-2 border-orange-200" data-id="element-1963">
                    <span className="text-3xl" data-id="element-1964">{transportEvent.emoji}</span>
                  </motion.div>
                  <div className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-600 font-bold text-xs uppercase tracking-wider mb-3" data-id="element-1965">
                    🚗 Transportation
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1966">
                    {transportEvent.name}
                  </h3>
                  <motion.div initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1,
              x: [0, -3, 3, -3, 0]
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="text-2xl font-bold text-orange-600 mb-5 py-2 px-4 bg-orange-50 rounded-2xl border-2 border-orange-200 inline-block" data-id="element-1967">
                    -${Math.abs(transportEvent.amount)}
                  </motion.div>
                  <p className="text-gray-400 text-sm font-medium mb-5" data-id="element-1968">
                    Life just charged you again 😒
                  </p>
                  <motion.button onClick={dismissTransportEvent} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-3 rounded-full bg-gradient-to-r from-orange-400 to-amber-500 text-white font-bold text-base shadow-lg shadow-orange-400/20" data-id="element-1969">
                    Ugh, fine 😒
                  </motion.button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Bottom Bar */}
          <div className="bg-white/60 backdrop-blur-2xl border-t border-white/70 p-3 md:p-4 z-20 shadow-[0_-10px_40px_rgba(168,85,247,0.06)]" data-id="element-1970">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1971">
              <div className="flex flex-wrap justify-center gap-2 flex-1" data-id="element-1972">
                {players.map((player, i) => {
              const isEliminated = eliminatedPlayers.includes(player.id);
              return <motion.div key={player.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} className={`flex items-center gap-2 ${isEliminated ? 'opacity-40 grayscale' : ''}`} data-id="element-1973">
                      <div className={`w-6 h-6 rounded-full ${isEliminated ? 'bg-gray-400' : PLAYER_COLORS[player.color]} flex items-center justify-center text-white font-bold text-xs`} data-id="element-1974">
                        {isEliminated ? '💀' : player.name.charAt(0)}
                      </div>
                      <span className={`text-sm font-bold ${isEliminated ? 'text-gray-500 line-through' : player.money < 0 ? 'text-red-500' : 'text-emerald-600'}`} data-id="element-1975">
                        ${player.money}
                      </span>
                    </motion.div>;
            })}
              </div>
              <div className="flex items-center gap-2" data-id="element-1976">
                <button onClick={rollDice} disabled={isRolling} className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${isRolling ? 'bg-gray-200 text-gray-400' : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-lg'}`} data-id="element-1977">
                  {isRolling ? 'Rolling...' : '🎲 Roll Dice'}
                </button>
                <button onClick={startActualPlaying} disabled={isRolling} className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${isRolling ? 'bg-gray-200 text-gray-400' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:shadow-lg'}`} data-id="element-1978">
                  {isRolling ? 'Playing...' : '▶️ Start Game'}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* ========== CHAOS REALM ========== */}
      {phase === 'chaos_realm' && <div className="absolute inset-0 bg-gradient-to-b from-red-950 via-black to-red-950 flex flex-col overflow-hidden z-20" data-id="element-1979">
          <FloatingParticles count={60} data-id="element-1980" />

          {/* Entry Transition Overlay */}
          <AnimatePresence data-id="element-1981">
            {showChaosTransition && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black" data-id="element-1982">
                <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="text-center" data-id="element-1983">
                  <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 2
            }} className="text-8xl mb-6" data-id="element-1984">
                    🔥
                  </motion.div>
                  <h1 className="text-4xl md:text-6xl font-bold text-red-600 tracking-widest uppercase" style={{
              textShadow: '0 0 30px rgba(220,38,38,0.8)'
            }} data-id="element-1985">
                    Entering the
                    <br data-id="element-1986" />
                    Chaos Realm
                  </h1>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {/* Header */}
          <div className="p-4 bg-black/50 backdrop-blur-md border-b border-red-900/50 z-30 flex justify-between items-center" data-id="element-1987">
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-1988">
              🔥 Chaos Realm
            </h2>
            <div className="flex items-center gap-4" data-id="element-1989">
              <div className="flex items-center gap-2" data-id="element-1990">
                {players[currentPlayerIndex].avatar ? <img src={players[currentPlayerIndex].avatar!} alt="" className={`w-8 h-8 rounded-full border-2 ${PLAYER_BORDER[players[currentPlayerIndex].color]}`} data-id="element-1991" /> : <div className={`w-8 h-8 rounded-full ${PLAYER_COLORS[players[currentPlayerIndex].color]} flex items-center justify-center text-white font-bold text-sm`} data-id="element-1992">
                    {players[currentPlayerIndex].name.charAt(0)}
                  </div>}
                <span className="text-white font-bold" data-id="element-1993">
                  {players[currentPlayerIndex].name}
                </span>
              </div>
              <div className="bg-red-950/80 px-4 py-1.5 rounded-full border border-red-800" data-id="element-1994">
                <span className="text-red-400 text-sm font-bold" data-id="element-1995">
                  Realm Loss:{' '}
                </span>
                <span className="text-red-500 font-bold" data-id="element-1996">
                  ${Math.abs(realmTotalLoss)}
                </span>
              </div>
            </div>
          </div>

          {/* Chaos Board */}
          <div className="flex-1 relative overflow-y-auto overflow-x-hidden p-8 flex flex-col items-center" data-id="element-1997">
            <div className="relative w-full max-w-md flex flex-col items-center gap-8 pb-32 pt-10" data-id="element-1998">
              {/* Path connecting line */}
              <div className="absolute top-0 bottom-0 w-2 bg-gradient-to-b from-red-900/20 via-red-500/20 to-red-900/20 left-1/2 -translate-x-1/2 rounded-full" data-id="element-1999" />

              {chaosRealmTiles.map((tile, idx) => {
            const isCurrent = idx === chaosRealmPosition;
            const isPast = idx < chaosRealmPosition;
            const isFuture = idx > chaosRealmPosition;
            return <motion.div key={idx} ref={isCurrent ? activeChaosTileRef : null} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`relative w-full p-5 rounded-2xl border-2 transition-all duration-500 z-10 ${isCurrent ? 'bg-red-950/90 border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.4)] scale-105' : isPast ? 'bg-black/60 border-red-900/50 opacity-60' : 'bg-black/80 border-red-900/30'}`} data-id="element-2000">
                    {/* Player Piece on Current Tile */}
                    {isCurrent && <motion.div layoutId="chaosPlayerPiece" className={`absolute -left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 ${PLAYER_BORDER[players[currentPlayerIndex].color]} shadow-[0_0_20px_rgba(255,255,255,0.3)] z-20 ${!players[currentPlayerIndex].avatar ? PLAYER_COLORS[players[currentPlayerIndex].color] : 'bg-white'}`} data-id="element-2001">
                        {players[currentPlayerIndex].avatar ? <img src={players[currentPlayerIndex].avatar!} alt="" className="w-full h-full rounded-full object-cover" data-id="element-2002" /> : <div className="w-full h-full flex items-center justify-center text-white font-bold" data-id="element-2003">
                            {players[currentPlayerIndex].name.charAt(0)}
                          </div>}
                      </motion.div>}

                    {isFuture ? <div className="flex items-center justify-center gap-3 py-2" data-id="element-2004">
                        <span className="text-3xl opacity-50" data-id="element-2005">💀</span>
                        <span className="text-red-800 font-bold text-xl tracking-widest" data-id="element-2006">
                          ???
                        </span>
                      </div> : <div className="flex flex-col gap-2 pl-6" data-id="element-2007">
                        <div className="flex items-center gap-3" data-id="element-2008">
                          <span className="text-3xl" data-id="element-2009">{tile.emoji}</span>
                          <h3 className={`font-bold text-lg ${isCurrent ? 'text-white' : 'text-gray-400'}`} data-id="element-2010">
                            {tile.name}
                          </h3>
                        </div>
                        <p className={`text-sm ${isCurrent ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2011">
                          {tile.description}
                        </p>
                        {tile.effectValue !== 0 && <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold w-max mt-1 ${tile.effect === 'money_loss' ? 'bg-red-900/50 text-red-400 border border-red-800' : tile.effect === 'recovery' ? 'bg-green-900/50 text-green-400 border border-green-800' : 'bg-gray-800 text-gray-400'}`} data-id="element-2012">
                            {tile.effect === 'money_loss' ? `-$${Math.abs(tile.effectValue)}` : tile.effect === 'recovery' ? `+$${tile.effectValue}` : tile.effect === 'move_back' ? `Move back ${Math.abs(tile.effectValue)}` : ''}
                          </div>}
                        {tile.effect === 'instant_lose' && <div className="inline-block px-3 py-1 rounded-full text-xs font-bold w-max mt-1 bg-red-600 text-white animate-pulse" data-id="element-2013">
                            INSTANT ELIMINATION
                          </div>}
                      </div>}
                  </motion.div>;
          })}
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="p-6 bg-black/80 backdrop-blur-md border-t border-red-900/50 z-30 flex justify-center" data-id="element-2014">
            {chaosRealmTiles[chaosRealmPosition]?.effect !== 'exit' && chaosRealmTiles[chaosRealmPosition]?.effect !== 'instant_lose' ? <div className="flex flex-col items-center gap-4" data-id="element-2015">
                {chaosRealmDiceValue && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="text-4xl font-bold text-red-500 bg-red-950/50 w-16 h-16 rounded-xl flex items-center justify-center border border-red-800" data-id="element-2016">
                    {chaosRealmDiceValue}
                  </motion.div>}
                <motion.button onClick={rollChaosDice} disabled={chaosRealmRolling} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className={`px-10 py-4 rounded-full font-bold text-xl shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all ${chaosRealmRolling ? 'bg-red-950 text-red-800 border border-red-900' : 'bg-gradient-to-r from-red-600 to-red-800 text-white border border-red-500 hover:from-red-500 hover:to-red-700'}`} data-id="element-2017">
                  {chaosRealmRolling ? 'Rolling...' : '🎲 Roll (1-3)'}
                </motion.button>
              </div> : <div className="text-center" data-id="element-2018">
                <p className="text-white font-bold text-xl mb-2" data-id="element-2019">
                  {chaosRealmTiles[chaosRealmPosition]?.effect === 'exit' ? 'You survived the Chaos Realm!' : '...'}
                </p>
              </div>}
          </div>

          {/* Elimination Overlay */}
          <AnimatePresence data-id="element-2020">
            {showElimination && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md" data-id="element-2021">
                <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1,
            x: [0, -10, 10, -10, 10, 0],
            y: [0, 10, -10, 10, -10, 0]
          }} transition={{
            duration: 0.5
          }} className="text-center" data-id="element-2022">
                  <span className="text-8xl block mb-6" data-id="element-2023">💀</span>
                  <h1 className="text-6xl md:text-8xl font-bold text-red-600 mb-4 tracking-widest uppercase" style={{
              textShadow: '0 0 40px rgba(220,38,38,0.8)'
            }} data-id="element-2024">
                    Eliminated
                  </h1>
                  <p className="text-2xl text-red-400 font-bold" data-id="element-2025">
                    {eliminatedPlayerName} lost everything to the Chaos Realm.
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>}

      {/* ========== CAREER SWITCH ========== */}
      {phase === 'career_switch' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-2026">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-3xl w-full text-center relative overflow-y-auto max-h-[90vh]" data-id="element-2027">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/20 rounded-full blur-3xl" data-id="element-2028" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/20 rounded-full blur-3xl" data-id="element-2029" />

            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-2030">
              Career Switch
            </h2>
            <p className="text-purple-400 font-bold mb-8" data-id="element-2031">
              You're switching careers! Choose your new path...
            </p>

            <div className="space-y-4" data-id="element-2032">
              {realmEvents.map((event, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="p-4 rounded-2xl border-2 bg-yellow-50 border-yellow-200" data-id="element-2033">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2034">
                    <span className="text-2xl" data-id="element-2035">💼</span>
                    <span className="font-bold text-gray-800" data-id="element-2036">
                      {event.name}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-2037">
                    You lost{' '}
                    <span className="font-bold text-red-600" data-id="element-2038">
                      ${Math.abs(event.amount)}
                    </span>
                  </p>
                </motion.div>)}
            </div>

            <div className="mt-6" data-id="element-2039">
              <p className="text-lg text-gray-700 mb-4" data-id="element-2040">
                You're switching from your current path to a new one. Choose
                wisely...
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2041">
                {LIFE_PATHS.map((path, i) => <motion.button key={i} onClick={() => handleCareerSwitchSelection(i)} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} className={`p-4 rounded-2xl border-2 ${pendingNewPath === i ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' : 'bg-white/80 border-gray-200'} text-center`} data-id="element-2042">
                    <div className="flex items-center justify-center mb-2" data-id="element-2043">
                      <span className="text-2xl" data-id="element-2044">{path.emoji}</span>
                    </div>
                    <p className="font-bold text-lg" data-id="element-2045">{path.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-2046">
                      {pathJobs.map(job => <span key={job.id} className="inline-block mr-1" data-id="element-2047">
                          {job.emoji} {job.name}
                        </span>)}
                    </p>
                  </motion.button>)}
              </div>
              <motion.button onClick={confirmCareerSwitch} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="mt-4 px-8 py-4 rounded-full bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold text-lg shadow-lg" data-id="element-2048">
                Confirm Switch
              </motion.button>
            </div>
          </motion.div>
        </div>}

      {/* ========== TAX EVENT ========== */}
      {phase === 'tax_event' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-2049">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-3xl w-full text-center relative overflow-y-auto max-h-[90vh]" data-id="element-2050">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/20 rounded-full blur-3xl" data-id="element-2051" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/20 rounded-full blur-3xl" data-id="element-2052" />

            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-2053">
              Tax Season
            </h2>
            <p className="text-purple-400 font-bold mb-8" data-id="element-2054">
              All players get taxed! How much will you have left?
            </p>

            <div className="space-y-4" data-id="element-2055">
              {taxResults.map((result, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="p-4 rounded-2xl border-2 bg-red-50 border-red-200" data-id="element-2056">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2057">
                    <span className="text-2xl" data-id="element-2058">💸</span>
                    <span className="font-bold text-gray-800" data-id="element-2059">
                      {result.name}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-2060">
                    {result.emoji} {result.taxType} - ${result.taxed} remaining:
                    ${result.remaining}
                  </p>
                </motion.div>)}
            </div>

            <div className="mt-6" data-id="element-2061">
              <motion.button onClick={closeTaxEvent} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-8 py-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-lg shadow-lg" data-id="element-2062">
                Close Tax Event
              </motion.button>
            </div>
          </motion.div>
        </div>}

      {/* ========== WINNER ========== */}
      {phase === 'winner' && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-2063">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="bg-white/70 backdrop-blur-2xl p-8 md:p-12 rounded-[2rem] shadow-[0_20px_80px_rgba(168,85,247,0.15)] border border-white/80 max-w-3xl w-full text-center relative overflow-y-auto max-h-[90vh]" data-id="element-2064">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-pink-300/20 rounded-full blur-3xl" data-id="element-2065" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-300/20 rounded-full blur-3xl" data-id="element-2066" />

            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-2067">
              Congratulations!
            </h2>
            <p className="text-purple-400 font-bold mb-8" data-id="element-2068">
              {winner?.name} has won the game!
            </p>

            <div className="space-y-4" data-id="element-2069">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="p-4 rounded-2xl border-2 bg-green-50 border-green-200" data-id="element-2070">
                <div className="flex items-center gap-2 mb-2" data-id="element-2071">
                  <span className="text-2xl" data-id="element-2072">🏆</span>
                  <span className="font-bold text-gray-800" data-id="element-2073">Final Results</span>
                </div>
                <p className="text-sm text-gray-500" data-id="element-2074">
                  {winner?.name} has ${winner?.money} at the end!
                </p>
              </motion.div>
            </div>

            <div className="mt-6" data-id="element-2075">
              <motion.button onClick={() => {
            setPhase('setup');
            setPlayers([{
              id: '1',
              name: 'Player 1',
              color: 'pink',
              position: 0,
              skipNextTurn: false,
              avatar: null,
              job: null,
              money: 0,
              turnsPlayed: 0,
              roundsWithoutIncome: 0,
              evolved: false,
              pathIndex: null,
              inSchool: false,
              schoolTurnsLeft: 0,
              studentLoanDebt: 0,
              chaosTriggersRemaining: 0
            }, {
              id: '2',
              name: 'Player 2',
              color: 'purple',
              position: 0,
              skipNextTurn: false,
              avatar: null,
              job: null,
              money: 0,
              turnsPlayed: 0,
              roundsWithoutIncome: 0,
              evolved: false,
              pathIndex: null,
              inSchool: false,
              schoolTurnsLeft: 0,
              studentLoanDebt: 0,
              chaosTriggersRemaining: 0
            }]);
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-8 py-4 rounded-full bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold text-lg shadow-lg" data-id="element-2076">
                Play Again
              </motion.button>
            </div>
          </motion.div>
        </div>}
    </div>;
}